import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { SessionPeriodChip } from './UI/SessionPeriodChip';
import ApprovalTimeline from './Leave/ApprovalTimeline';
import ApprovalStepper from './Leave/ApprovalStepper';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staffAPI } from '../services/api';
import { CheckCircle, Clock, Upload, FileText, XCircle, AlertTriangle, Send, X, Home, Plane, PlusCircle, ChevronRight, ArrowRight, Smartphone, ZoomIn, ZoomOut, RefreshCw, Eye, Ban, Info } from 'lucide-react';
import toast from '../utils/toast';
import { parseApiValidationErrors } from '../utils/apiErrors';
import Portal from './UI/Portal';
import LoadingSpinner from './UI/LoadingSpinner';
import PdfViewer from './UI/PdfViewer';
import ConfirmModal from './UI/ConfirmModal';
import { formatDate, formatDateTimeCompact, parseLocalDate, toLocalISODate, formatDateForInput, getTodayInSingaporeTimezone } from '../utils/dateUtils';
import { pluralizeDays, formatLeaveDays } from '../utils/numberUtils';
import FormSelect from './UI/FormSelect';
import { StatusBadge } from './UI/StatusBadge';
import { InfoBadge } from './UI/InfoBadge';
import DatePickerField from './UI/DatePickerField';
import DatalistCombobox from './UI/DatalistCombobox';
import MobileVerificationTabs from './UI/MobileVerificationTabs';
import VerificationPanelHeader from './UI/VerificationPanelHeader';
import { useIsMobile } from '../hooks/useIsMobile';
import { useSafeDiscardAndClose } from '../hooks/useSafeDiscardAndClose';
import { useImageViewer } from '../hooks/useImageViewer';
import { getLeaveTypeBadgeClass } from '../utils/leaveTypeBadgeColors';
import { LeaveTypeBadge } from './UI/LeaveTypeBadge';
import { normalizeHeicFileForUpload } from '../utils/imageUtils';
import { canCancelLeave } from '../utils/leavePermissions';
import LeaveExcludedDaysCallout from './Leave/LeaveExcludedDaysCallout';
import ContinuousHomeLeaveBlocker from './Leave/ContinuousHomeLeaveBlocker';
import useLeaveExcludedDates from '../hooks/useLeaveExcludedDates';
import {
  formatConsumptionProposalText,
  getOverseasLeaveSplitNotes,
  hasCrossTypeConsumption,
} from '../utils/leaveDisplayUtils';
import ConsumptionBreakdown, { DurationWithConsumption } from './Leave/ConsumptionBreakdown';
import {
  AIRPORT_SUGGESTIONS,
  FLIGHT_TRIP_TYPE_LABELS,
  isOneWayOutboundTrip,
} from '../utils/flightTicketUtils';
import {
  useLeaveExtensionEligibility,
  useLeaveExtensionPreview,
  useRequestLeaveExtension,
} from '../hooks/useLeaveData';

// `currentStep` is ALWAYS the authoritative workflow position — never the
// peeked step. `viewingStep` (nullable) marks a completed step the worker is
// peeking at: it gets a steady ring while the current step keeps its pulse,
// and the current step stays clickable so the worker can return by tapping it.
const HorizontalStepper = ({ steps, currentStep, viewingStep = null, onStepClick }) => {
  const isPeeking = viewingStep !== null && viewingStep !== currentStep;
  return (
    <div className="flex items-start justify-between mb-6">
      {steps.map((step, index) => {
        const isComplete = step.id < currentStep;
        const isCurrent = step.id === currentStep;
        const isViewing = isPeeking && step.id === viewingStep;
        // Completed steps open a peek; the current step returns from one.
        const isClickable = !!onStepClick && ((isComplete && !isViewing) || (isCurrent && isPeeking));
        const Icon = step.icon;
        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <button
                type="button"
                disabled={!isClickable}
                onClick={() => isClickable && onStepClick(step.id)}
                title={
                  isViewing ? 'Currently viewing'
                    : isCurrent && isPeeking ? 'Back to current step'
                    : isClickable ? `View ${step.name}`
                    : undefined
                }
                className={`
                  w-10 h-10 rounded-full flex items-center justify-center text-white transition-all duration-200
                  ${isComplete ? 'bg-blue-800 shadow-sm' : isCurrent ? 'bg-blue-800 shadow-sm ring-4 ring-blue-200 animate-pulse' : 'bg-gray-300'}
                  ${isViewing ? 'ring-4 ring-blue-300' : ''}
                  ${isClickable ? 'cursor-pointer hover:ring-4 hover:ring-blue-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2' : 'cursor-default'}
                `}
              >
                {isComplete ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
              </button>
              <span className={`text-xs mt-2 text-center max-w-[60px] leading-tight ${isCurrent || isViewing ? 'font-semibold text-blue-800' : isComplete ? 'text-blue-700' : 'text-gray-500'}`}>{step.name}</span>
            </div>
            {index < steps.length - 1 && (
              <div className={`flex-1 h-1 rounded-full mx-2 mt-[18px] transition-all duration-300 ${step.id < currentStep ? 'bg-blue-800' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Annual Leave reason options
const ANNUAL_LEAVE_REASONS = [
  { value: '', label: 'Select a reason...' },
  { value: 'personal', label: 'Personal matters' },
  { value: 'family', label: 'Family commitment' },
  { value: 'medical', label: 'Medical appointment' },
  { value: 'travel', label: 'Travel / Vacation' },
  { value: 'rest', label: 'Rest and recuperation' },
  { value: 'other', label: 'Other' }
];

// Urgent Leave reason options
const URGENT_LEAVE_REASONS = [
  { value: '', label: 'Select a reason...' },
  { value: 'death_family', label: 'Death of family member' },
  { value: 'medical_emergency', label: 'Medical emergency (family)' },
  { value: 'natural_disaster', label: 'Natural disaster' },
  { value: 'other', label: 'Other' }
];

// Per-trip Home Leave ceiling (AL + HL combined). The yearly HL+UL levy
// waiver is 60 days — that is NOT the trip picker max.
const HOME_LEAVE_TRIP_CAP = 30;

const addInclusiveCalendarDays = (start, inclusiveDays) => {
  const next = new Date(start);
  next.setDate(next.getDate() + Math.max(inclusiveDays - 1, 0));
  return next;
};

// Cap for the year the trip STARTS in. This year's card is not next year's cap.
const homeLeaveCapForYear = (eligibility, availableTypes, isoDate) => {
  const parsed = isoDate ? parseLocalDate(isoDate) : null;
  const currentYear = new Date().getFullYear();
  const year = parsed ? parsed.getFullYear() : currentYear;
  if (year > currentYear + 1) return 0;

  let remaining;
  if (year > currentYear) {
    remaining = Number(eligibility?.days_bookable_next_year);
    if (!Number.isFinite(remaining)) {
      remaining = Number(eligibility?.days_remaining_next_year);
    }
    if (!Number.isFinite(remaining)) remaining = HOME_LEAVE_TRIP_CAP;
  } else {
    remaining = Number(eligibility?.days_bookable_this_year);
    if (!Number.isFinite(remaining)) {
      remaining = Number(eligibility?.days_remaining_this_year);
    }
    if (!Number.isFinite(remaining)) {
      const hlType = (availableTypes || []).find(
        (t) => (t.leave_type_name || t.name || '').toLowerCase() === 'home leave'
      );
      remaining = Number(hlType?.remaining_balance);
    }
    if (!Number.isFinite(remaining)) remaining = HOME_LEAVE_TRIP_CAP;
  }
  return Math.max(0, Math.min(HOME_LEAVE_TRIP_CAP, Math.floor(remaining)));
};

const Step1ApplicationForm = ({ onSubmitted, initialDates, leaveType, submitRef, onDisabledChange }) => {
  const [leaveStart, setLeaveStart] = useState(initialDates?.start || '');
  const [leaveEnd, setLeaveEnd] = useState(initialDates?.end || '');
  const [leaveStartTimePeriod, setLeaveStartTimePeriod] = useState('AM');
  const [leaveEndTimePeriod, setLeaveEndTimePeriod] = useState('PM');
  const [dateError, setDateError] = useState('');
  const [calculatedEndDate, setCalculatedEndDate] = useState('');
  const [alPreview, setAlPreview] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculatedLeaveDays, setCalculatedLeaveDays] = useState(0);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcFailed, setCalcFailed] = useState(false);
  const [excludedLeaveDates, setExcludedLeaveDates] = useState([]);
  const [proposalDisplay, setProposalDisplay] = useState(''); // OIL→AL split display
  const [futureYearProjection, setFutureYearProjection] = useState(null);
  const [unpaidDays, setUnpaidDays] = useState(0);
  const [leaveReason, setLeaveReason] = useState(''); // Annual Leave / Urgent Leave reason
  const [otherReason, setOtherReason] = useState(''); // For "Other" reason details
  const [submitError, setSubmitError] = useState('');

  const isHomeLeave = leaveType === 'home_leave';
  const isAnnualLeave = leaveType === 'annual_leave';
  const isUrgentLeave = leaveType === 'urgent_leave';

  // Resolve leave_type_id based on leaveType prop
  const { data: eligibilityData } = useQuery({
    queryKey: ['leave', 'available-types'],
    queryFn: async () => {
      const response = await staffAPI.getAvailableLeaveTypes();
      const leaveTypes = response.data?.available_leave_types || [];
      const uniqueLeaveTypes = Array.from(
        new Map(leaveTypes.map(type => [type.id, type])).values()
      );
      return {
        leaveTypes: uniqueLeaveTypes,
        recommendations: response.data?.recommendations || {},
        eligibilityDetails: response.data?.eligibility_details || {},
      };
    },
  });
  const availableTypes = eligibilityData?.leaveTypes || [];
  
  const leaveTypeId = useMemo(() => {
    const targetName = isHomeLeave ? 'home leave' : isUrgentLeave ? 'urgent leave' : 'annual leave';
    const match = availableTypes.find(t => (t.leave_type_name || t.name || '').toLowerCase() === targetName);
    return match?.id || null;
  }, [availableTypes, isHomeLeave, isUrgentLeave]);
  
  // HOME LEAVE: advance-notice waiver — a worker already overseas on an active
  // Urgent/Home Leave can apply for Home Leave directly to continue their stay,
  // bypassing the usual 30-day notice they could not have given while abroad.
  // Backend mirrors this in HomeLeaveEligibilityService.get_active_overseas_leave.
  const homeLeaveEligibility = eligibilityData?.eligibilityDetails?.home_leave || null;
  const advanceNoticeWaived = isHomeLeave && !!homeLeaveEligibility?.advance_notice_waived;
  const activeOverseasLeave = homeLeaveEligibility?.active_overseas_leave || null;

  // HOME LEAVE: earliest start is the later of notice, next year's cap
  // (when this year is used up), and the 12-month service date.
  const getMinDate = () => {
    if (!isHomeLeave) return undefined;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let minDate = new Date(today);
    if (!advanceNoticeWaived) minDate.setDate(minDate.getDate() + 30);

    const thisBookable = Number(homeLeaveEligibility?.days_bookable_this_year);
    if (Number.isFinite(thisBookable) && thisBookable <= 0) {
      const nextYearStart = new Date(today.getFullYear() + 1, 0, 1);
      if (nextYearStart > minDate) minDate = nextYearStart;
    }
    const serviceFrom = parseLocalDate(homeLeaveEligibility?.service_eligible_from);
    if (serviceFrom && serviceFrom > minDate) minDate = serviceFrom;
    return formatDateForInput(minDate);
  };

  const homeLeaveMaxStart = useMemo(() => {
    return new Date(new Date().getFullYear() + 1, 11, 31);
  }, []);
  
  // HOME LEAVE: Validate start date (30-day advance notice; waived when overseas)
  const validateStartDate = (date) => {
    if (!isHomeLeave || !date) return true;

    const selected = parseLocalDate(date);
    if (!selected) return true;
    const currentYear = new Date().getFullYear();
    if (selected.getFullYear() > currentYear + 1) {
      setDateError(`Home Leave can be submitted for ${currentYear} or ${currentYear + 1}.`);
      return false;
    }

    const earliest = parseLocalDate(getMinDate());
    if (earliest && selected < earliest) {
      const serviceFrom = parseLocalDate(homeLeaveEligibility?.service_eligible_from);
      const thisBookable = Number(homeLeaveEligibility?.days_bookable_this_year);
      if (serviceFrom && selected < serviceFrom) {
        setDateError(`Home Leave starts after 12 months of service. Earliest date: ${formatDate(serviceFrom)}.`);
      } else if (Number.isFinite(thisBookable) && thisBookable <= 0 && selected.getFullYear() === currentYear) {
        setDateError(`Your ${currentYear} Home Leave is already used. Earliest date: ${formatDate(earliest)}.`);
      } else if (!advanceNoticeWaived) {
        setDateError(`Home Leave requires 30 days advance notice. Earliest date: ${formatDate(earliest)}.`);
      } else {
        setDateError(`Earliest start date is ${formatDate(earliest)}.`);
      }
      return false;
    }
    
    setDateError('');
    return true;
  };
  
  // Cap follows the year the trip starts in, not the balance card on screen.
  const homeLeaveCapDays = useMemo(() => {
    if (!isHomeLeave) return HOME_LEAVE_TRIP_CAP;
    return homeLeaveCapForYear(homeLeaveEligibility, availableTypes, leaveStart);
  }, [isHomeLeave, homeLeaveEligibility, availableTypes, leaveStart]);

  // Latest return = start + (cap - 1). Inclusive: 24 Oct + 29 = 22 Nov (30 days).
  const homeLeaveMaxEndDate = useMemo(() => {
    if (!isHomeLeave || !leaveStart) return undefined;
    const start = parseLocalDate(leaveStart);
    if (!start) return undefined;
    return addInclusiveCalendarDays(start, homeLeaveCapDays);
  }, [isHomeLeave, leaveStart, homeLeaveCapDays]);

  // HOME LEAVE: live calendar day counter (pure client-side, no API call)
  const homeLeaveCalendarDays = useMemo(() => {
    if (!isHomeLeave || !leaveStart || !leaveEnd) return 0;
    const start = parseLocalDate(leaveStart);
    const end = parseLocalDate(leaveEnd);
    if (!start || !end) return 0;
    return Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
  }, [isHomeLeave, leaveStart, leaveEnd]);

  const homeLeaveTripYear = leaveStart ? parseLocalDate(leaveStart)?.getFullYear() : null;
  const homeLeaveCurrentYear = new Date().getFullYear();
  const homeLeaveBooksNextYear = Boolean(
    isHomeLeave && (
      (homeLeaveTripYear && homeLeaveTripYear > homeLeaveCurrentYear)
      || (
        !leaveStart
        && Number(homeLeaveEligibility?.days_bookable_this_year) <= 0
        && Number(homeLeaveEligibility?.days_bookable_next_year) > 0
      )
    )
  );
  const homeLeaveBalanceYear = (homeLeaveTripYear && homeLeaveTripYear > homeLeaveCurrentYear)
    ? homeLeaveTripYear
    : (homeLeaveBooksNextYear ? homeLeaveCurrentYear + 1 : homeLeaveCurrentYear);

  const homeLeaveOverCap = Boolean(
    isHomeLeave && leaveStart && leaveEnd && homeLeaveCalendarDays > homeLeaveCapDays
  );
  const homeLeaveCapError = homeLeaveCapDays <= 0 && leaveStart
    ? `You have no Home Leave left for ${homeLeaveBalanceYear}.`
    : homeLeaveOverCap
      ? `This trip is ${homeLeaveCalendarDays} days. Home Leave allows up to ${homeLeaveCapDays} calendar days in ${homeLeaveBalanceYear}. Latest return: ${formatDate(homeLeaveMaxEndDate)}.`
      : '';

  // Clamp an over-long end date (preselected range, or start moved later).
  useEffect(() => {
    if (!isHomeLeave || !leaveStart || !leaveEnd || !homeLeaveMaxEndDate) return;
    const end = parseLocalDate(leaveEnd);
    if (end && end > homeLeaveMaxEndDate) {
      setLeaveEnd(toLocalISODate(homeLeaveMaxEndDate));
    }
  }, [isHomeLeave, leaveStart, leaveEnd, homeLeaveMaxEndDate]);

  // HOME LEAVE: fetch AL/HL split preview whenever both dates are set
  const [alPreviewLoading, setAlPreviewLoading] = useState(false);
  useEffect(() => {
    if (!isHomeLeave) return;
    if (!leaveStart || !leaveEnd) { setAlPreview(null); return; }
    let cancelled = false;
    setAlPreviewLoading(true);
    (async () => {
      try {
        const resp = await staffAPI.getHomeLeaveALPreview(leaveStart, leaveEnd);
        if (!cancelled) setAlPreview(resp.data);
      } catch (err) {
        if (!cancelled) {
          console.error('Home leave AL preview failed:', err);
          setAlPreview(null);
        }
      } finally {
        if (!cancelled) setAlPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isHomeLeave, leaveStart, leaveEnd]);

  // URGENT LEAVE: Calculate end date based on 14 calendar days
  const calculateUrgentLeaveEndDate = async (startDate) => {
    if (!startDate) return;
    
    setIsCalculating(true);
    try {
      const response = await staffAPI.calculateUrgentLeaveEndDate(startDate);
      setCalculatedEndDate(response.data.end_date);
      setLeaveEnd(response.data.end_date);
      
      // Also fetch AL -> UL preview
      const previewResponse = await staffAPI.getUrgentLeaveALPreview();
      setAlPreview(previewResponse.data);
    } catch (error) {
      toast.error('Failed to calculate end date');
      console.error('Calculate urgent leave end date error:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  // ANNUAL LEAVE: Calculate leave days using backend API
  useEffect(() => {
    if (!isAnnualLeave || !leaveStart || !leaveEnd) {
      if (isAnnualLeave) {
        setCalculatedLeaveDays(0);
        setProposalDisplay('');
        setFutureYearProjection(null);
        setUnpaidDays(0);
        setExcludedLeaveDates([]);
        setCalcFailed(false);
      }
      return;
    }

    let cancelled = false;
    const calculateDays = async () => {
      setCalcLoading(true);
      setProposalDisplay('');
      setFutureYearProjection(null);
      setUnpaidDays(0);
      setCalcFailed(false);
      try {
        const payload = {
          start_date: leaveStart,
          end_date: leaveEnd,
          leave_type_name: 'Annual Leave'
        };
        const resp = await staffAPI.calculateLeaveDays(payload);
        if (cancelled) return;
        let days = resp?.data?.calculation?.mom_compliant_days ?? 0;

        // Apply frontend AM/PM adjustments
        if (leaveStartTimePeriod === 'PM') days -= 0.5;
        if (leaveEndTimePeriod === 'AM') days -= 0.5;

        const finalDays = Math.max(0, days);
        setCalculatedLeaveDays(finalDays);
        setExcludedLeaveDates(resp?.data?.calculation?.excluded_dates || []);

        // Fetch OIL→AL consumption proposal
        if (finalDays > 0) {
          try {
            const proposalResp = await staffAPI.proposeConsumption({
              start_date: leaveStart,
              end_date: leaveEnd,
              total_days: finalDays,
              start_time_period: leaveStartTimePeriod || undefined,
              end_time_period: leaveEndTimePeriod || undefined,
            });
            if (cancelled) return;
            const proposal = proposalResp?.data?.proposal || {};
            const items = proposal.items || [];
            const projection = proposal.future_year_projection || null;
            setFutureYearProjection(projection);
            if (
              Math.max(
                parseLocalDate(leaveStart)?.getFullYear() || 0,
                parseLocalDate(leaveEnd)?.getFullYear() || 0
              ) > new Date().getFullYear()
              && !projection
            ) {
              setCalcFailed(true);
              setSubmitError(
                'Could not calculate your next-year Annual Leave balance. Change the dates to try again.'
              );
            }
            const visibleItems = items.filter(
              (item) => Number(item?.days || 0) > 0
            );
            setProposalDisplay(
              visibleItems.length > 1
                ? formatConsumptionProposalText(visibleItems)
                : ''
            );
            setUnpaidDays(
              visibleItems
                .filter((item) => /unpaid/i.test(item.leave_type || ''))
                .reduce(
                  (sum, item) => sum + Number(item.days || 0),
                  0
                )
            );
            const proposalTotal = Number(proposal.total_days);
            if (Number.isFinite(proposalTotal) && proposalTotal > 0) {
              setCalculatedLeaveDays(proposalTotal);
            }
          } catch (proposalError) {
            if (cancelled) return;
            console.error('Fetch proposal error:', proposalError);
            setProposalDisplay('');
            setFutureYearProjection(null);
            setUnpaidDays(0);
            setCalcFailed(true);
            setSubmitError(
              'Could not calculate your Annual Leave balance. Change the dates to try again.'
            );
          }
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Calculate leave days error:', error);
        setCalculatedLeaveDays(0);
        setProposalDisplay('');
        setFutureYearProjection(null);
        setUnpaidDays(0);
        setExcludedLeaveDates([]);
        setCalcFailed(true);
        setSubmitError(
          'Could not calculate your Annual Leave days. Change the dates to try again.'
        );
      } finally {
        if (!cancelled) setCalcLoading(false);
      }
    };

    calculateDays();
    return () => {
      cancelled = true;
    };
  }, [isAnnualLeave, leaveStart, leaveEnd, leaveStartTimePeriod, leaveEndTimePeriod]);

  // Proactive overlap detection. Mirrors the staff SmartLeaveApplicationModal so a
  // worker sees a clash with an existing leave (e.g. an already-approved Home Leave)
  // BEFORE submitting, instead of only hitting the backend overlap guard (400).
  // Calendar-day types (Home/Urgent Leave) occupy full days → AM start / PM end.
  const [conflictCheck, setConflictCheck] = useState({
    conflicts: [],
    hasConflicts: false,
    continuousHomeLeave: null,
  });
  useEffect(() => {
    if (!leaveStart || !leaveEnd || parseLocalDate(leaveStart) > parseLocalDate(leaveEnd)) {
      setConflictCheck({
        conflicts: [],
        hasConflicts: false,
        continuousHomeLeave: null,
      });
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const resp = await staffAPI.checkLeaveConflicts({
          start_date: leaveStart,
          end_date: leaveEnd,
          leave_type_id: leaveTypeId || null,
          start_time_period: isAnnualLeave ? leaveStartTimePeriod : 'AM',
          end_time_period: isAnnualLeave ? leaveEndTimePeriod : 'PM',
        });
        if (cancelled) return;
        setConflictCheck({
          conflicts: resp.data?.conflicts || [],
          hasConflicts: resp.data?.has_conflicts || false,
          continuousHomeLeave: resp.data?.continuous_home_leave || null,
        });
      } catch (err) {
        if (!cancelled) {
          console.error('Leave conflict check failed:', err);
          setConflictCheck({
            conflicts: [],
            hasConflicts: false,
            continuousHomeLeave: null,
          });
        }
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [leaveStart, leaveEnd, leaveStartTimePeriod, leaveEndTimePeriod, leaveTypeId, isAnnualLeave]);

  const continuousHomeLeaveBlocked = Boolean(
    conflictCheck.continuousHomeLeave?.is_blocked
  );

  const handleSubmit = async () => {
    try {
      const leaveLabel = isHomeLeave ? 'Home Leave' : isUrgentLeave ? 'Urgent Leave' : 'Annual Leave';
      if (!leaveTypeId) {
        toast.error(`${leaveLabel} is not available for your profile`);
        return;
      }
      if (!leaveStart || !leaveEnd) {
        toast.error('Please select both start and end dates');
        return;
      }
      if (continuousHomeLeaveBlocked) {
        const message = conflictCheck.continuousHomeLeave.message;
        setSubmitError(message);
        toast.error(message);
        return;
      }
      if (conflictCheck.hasConflicts) {
        toast.error('These dates clash with leave you already have. Please adjust them.');
        return;
      }
      if (
        isAnnualLeave
        && futureYearProjection
        && !futureYearProjection.supported
      ) {
        const message = futureYearProjection.reason
          || 'A reliable paid balance is not available for those dates.';
        setSubmitError(message);
        toast.error(message);
        return;
      }
      if (isAnnualLeave && calcFailed) {
        const message = 'Could not calculate your Annual Leave balance. Change the dates to try again.';
        setSubmitError(message);
        toast.error(message);
        return;
      }
      if (isHomeLeave && homeLeaveOverCap) {
        setSubmitError(homeLeaveCapError);
        toast.error(homeLeaveCapError);
        return;
      }
      
      // Validate reason for Annual Leave
      if (isAnnualLeave) {
        if (!leaveReason) {
          toast.error('Please select a reason for your leave');
          return;
        }
        if (leaveReason === 'other' && !otherReason.trim()) {
          toast.error('Please specify your reason');
          return;
        }
      }

      // Validate reason for Urgent Leave (mandatory)
      if (isUrgentLeave) {
        if (!leaveReason) {
          toast.error('Please select a reason for your urgent leave');
          return;
        }
        if (leaveReason === 'other' && !otherReason.trim()) {
          toast.error('Please specify your reason');
          return;
        }
      }
      
      const payload = {
        leave_type_id: leaveTypeId,
        leave_start_date: leaveStart,
        leave_end_date: leaveEnd,
        leave_status: 'submitted'
      };

      // Add AM/PM periods and reason for Annual Leave
      if (isAnnualLeave) {
        payload.leave_start_time_period = leaveStartTimePeriod;
        payload.leave_end_time_period = leaveEndTimePeriod;
        
        // Build reason text
        const reasonLabel = ANNUAL_LEAVE_REASONS.find(r => r.value === leaveReason)?.label || leaveReason;
        payload.leave_reason = leaveReason === 'other' 
          ? `Other: ${otherReason.trim()}`
          : reasonLabel;
      }

      // Add reason for Urgent Leave
      if (isUrgentLeave) {
        const reasonLabel = URGENT_LEAVE_REASONS.find(r => r.value === leaveReason)?.label || leaveReason;
        payload.leave_reason = leaveReason === 'other'
          ? `Other: ${otherReason.trim()}`
          : reasonLabel;
      }

      const res = await staffAPI.createLeaveApplication(payload);
      setSubmitError('');
      toast.success('Application submitted successfully');
      onSubmitted(res.data);
    } catch (e) {
      // Worker mental model: "Request failed" means the app broke.
      // Never toast a sanitizer leftover when the form already knows why
      // (over-cap dates, notice, clash) or the API sent a real sentence.
      const parsed = parseApiValidationErrors(e);
      const message = parsed
        || (isHomeLeave && homeLeaveOverCap ? homeLeaveCapError : '')
        || dateError
        || (continuousHomeLeaveBlocked
          ? conflictCheck.continuousHomeLeave.message
          : '')
        || (conflictCheck.hasConflicts
          ? 'These dates clash with leave you already have. Please adjust them.'
          : '')
        || 'Could not submit this leave. Check the dates and try again.';
      setSubmitError(message);
      toast.error(message);
    }
  };

  const isSubmitDisabled = Boolean(
    !leaveStart ||
    !leaveEnd ||
    dateError ||
    homeLeaveOverCap ||
    continuousHomeLeaveBlocked ||
    conflictCheck.hasConflicts ||
    (isUrgentLeave && isCalculating) ||
    (isAnnualLeave && (calcLoading || calcFailed || calculatedLeaveDays <= 0)) ||
    (isAnnualLeave && futureYearProjection && !futureYearProjection.supported) ||
    (isAnnualLeave && !leaveReason) ||
    (isAnnualLeave && leaveReason === 'other' && !otherReason.trim()) ||
    (isUrgentLeave && !leaveReason) ||
    (isUrgentLeave && leaveReason === 'other' && !otherReason.trim())
  );

  // Wire submit handler and disabled state up to the parent modal footer
  useEffect(() => { if (submitRef) submitRef.current = handleSubmit; });
  useEffect(() => { onDisabledChange?.(isSubmitDisabled); }, [isSubmitDisabled, onDisabledChange]);
  useEffect(() => { setSubmitError(''); }, [leaveStart, leaveEnd, leaveReason, otherReason]);

  return (
    <div className="space-y-6">
      {submitError && submitError !== dateError && submitError !== homeLeaveCapError && (
        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg p-3" role="alert">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 leading-relaxed">{submitError}</p>
        </div>
      )}
      {/* HOME LEAVE: Side-by-side date pickers */}
      {isHomeLeave && (
        <>
          {advanceNoticeWaived && (
            <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <Info className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-800 leading-relaxed">
                {activeOverseasLeave ? (
                  <>
                    You're currently on{' '}
                    <span className="font-semibold">{activeOverseasLeave.leave_type}</span>
                    {activeOverseasLeave.end_date && (
                      <> until {formatDate(parseLocalDate(activeOverseasLeave.end_date))}</>
                    )}
                    . Because you're already overseas, you can apply for Home Leave now to
                    extend your stay — the usual 30-day advance notice doesn't apply.
                  </>
                ) : (
                  <>You're already overseas on approved leave, so you can apply for Home
                  Leave now to extend your stay — the usual 30-day advance notice doesn't
                  apply.</>
                )}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="form-group !mb-0">
              <label className="form-label">Start Date *</label>
              <DatePickerField
                theme="employee"
                selected={parseLocalDate(leaveStart)}
                minDate={parseLocalDate(getMinDate())}
                maxDate={homeLeaveMaxStart}
                onChange={(date) => {
                  const val = date ? toLocalISODate(date) : '';
                  setLeaveStart(val);
                  validateStartDate(val);
                  if (!val) {
                    setLeaveEnd('');
                    return;
                  }
                  // Tesler's Law: fill the latest valid return so the worker
                  // never has to count inclusive calendar days themselves.
                  // "24 Oct → 24 Nov" feels like a month; it is 32 days.
                  // Cap is the year of THIS start, not the year already on screen.
                  const start = parseLocalDate(val);
                  if (!start) return;
                  const cap = homeLeaveCapForYear(homeLeaveEligibility, availableTypes, val);
                  if (cap < 1) {
                    setLeaveEnd('');
                    return;
                  }
                  const maxEnd = addInclusiveCalendarDays(start, cap);
                  const end = parseLocalDate(leaveEnd);
                  if (!end || end < start || end > maxEnd) {
                    setLeaveEnd(toLocalISODate(maxEnd));
                  }
                }}
                placeholderText="Start date"
              />
            </div>
            <div className="form-group !mb-0">
              <label className="form-label">End Date *</label>
              <DatePickerField
                theme="employee"
                selected={parseLocalDate(leaveEnd)}
                minDate={parseLocalDate(leaveStart)}
                maxDate={homeLeaveMaxEndDate}
                disabled={!leaveStart}
                onChange={(date) => setLeaveEnd(date ? toLocalISODate(date) : '')}
                placeholderText={leaveStart ? 'End date' : 'Pick start first'}
              />
            </div>
          </div>
          {(dateError || homeLeaveCapError) ? (
            <p className="form-error flex items-start gap-1 -mt-4" role="alert">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{dateError || homeLeaveCapError}</span>
            </p>
          ) : leaveStart && homeLeaveMaxEndDate && homeLeaveCapDays > 0 ? (
            <p className="form-help -mt-4">
              Come back by {formatDate(homeLeaveMaxEndDate)} — {homeLeaveCapDays} calendar days in {homeLeaveBalanceYear}
              from {formatDate(parseLocalDate(leaveStart))} (start and return both count).
            </p>
          ) : null}
          {homeLeaveBooksNextYear && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start gap-3">
                <Info
                  className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-blue-900">
                    Using your {homeLeaveBalanceYear} Home Leave
                  </p>
                  <p className="text-sm text-blue-800 mt-1">
                    Home Leave resets on 1 January. Applying now does not reduce your {homeLeaveBalanceYear - 1} Home Leave.
                  </p>
                  {alPreview?.is_projected && alPreview?.annual_leave_projection?.supported && (
                    <p className="text-sm text-blue-800 mt-2">
                      Annual Leave inside this trip comes from your projected {homeLeaveBalanceYear} balance
                      {' '}({formatLeaveDays(alPreview.annual_leave_projection.projected_carry_forward_days)} days carry-forward
                      {' · '}
                      {formatLeaveDays(alPreview.annual_leave_projection.projected_earned_days)} days earned by your leave date).
                      It does not reduce your {homeLeaveBalanceYear - 1} Annual Leave.
                    </p>
                  )}
                  {Number(alPreview?.annual_leave_projection?.projected_forfeiture_days || 0) > 0 && (
                    <p className="text-sm font-medium text-blue-900 mt-2">
                      Your {alPreview.annual_leave_projection.source_year} balance is {formatLeaveDays(alPreview.annual_leave_projection.projected_forfeiture_days)} {pluralizeDays(alPreview.annual_leave_projection.projected_forfeiture_days)} above the carry-forward limit. This excess must be used by 31 December {alPreview.annual_leave_projection.source_year}. It will not carry into {homeLeaveBalanceYear} and cannot be used for leave dates from 1 January {homeLeaveBalanceYear}.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Consumption breakdown — replaces the plain calendar-days badge */}
          {leaveStart && leaveEnd && (
            <div className={`${homeLeaveOverCap ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'} border rounded-lg p-3 -mt-2`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-gray-700">Leave Breakdown</span>
                <InfoBadge
                  color={homeLeaveOverCap ? 'red' : 'blue'}
                  size="xs"
                  label={`${homeLeaveCalendarDays} / ${homeLeaveCapDays} calendar days`}
                />
              </div>
              {alPreviewLoading ? (
                <div className={`flex items-center gap-1.5 mt-2 text-xs ${homeLeaveOverCap ? 'text-red-700' : 'text-blue-700'}`}>
                  <LoadingSpinner size="xs" />
                  <span>Calculating breakdown...</span>
                </div>
              ) : alPreview ? (() => {
                const alDays = parseFloat(alPreview.annual_leave_to_consume) || 0;
                const hlDays = parseFloat(alPreview.home_leave_to_consume) || 0;
                const chips = [];
                if (alDays > 0) chips.push({ days: alDays, label: 'AL', type: 'Annual Leave' });
                if (hlDays > 0) chips.push({ days: hlDays, label: 'HL', type: 'Home Leave' });

                if (chips.length === 0) return null;

                return (
                  <div className="flex items-center gap-1.5 mt-2">
                    {chips.map((chip, idx) => (
                      <React.Fragment key={chip.label}>
                        {idx > 0 && <span className="text-gray-400 text-xs">+</span>}
                        <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ring-1 ring-inset ${getLeaveTypeBadgeClass(chip.type)}`}>
                          {formatLeaveDays(chip.days)} {chip.label}
                        </span>
                      </React.Fragment>
                    ))}
                  </div>
                );
              })() : (
                <p className="text-xs text-gray-500 mt-1.5">
                  {homeLeaveCalendarDays} calendar days (breakdown unavailable)
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* START DATE (non-Home Leave) */}
      {!isHomeLeave && (
      <div className="form-group">
        <label className="form-label">Start Date *</label>
        {isUrgentLeave ? (
          // Urgent Leave: No minimum date (no advance notice required)
          <DatePickerField
            theme="employee"
            selected={parseLocalDate(leaveStart)}
            onChange={(date) => {
              const val = date ? toLocalISODate(date) : '';
              setLeaveStart(val);
              calculateUrgentLeaveEndDate(val);
            }}
            placeholderText="Select start date"
          />
        ) : (
          // Annual Leave: DatePickerField with AM/PM selector
          <div className="flex gap-2">
            <div className="flex-1">
              <DatePickerField
                theme="employee"
                selected={parseLocalDate(leaveStart)}
                onChange={(date) => setLeaveStart(date ? toLocalISODate(date) : '')}
                placeholderText="Select start date"
              />
            </div>
            <div className="flex bg-gray-100 rounded-full p-1 w-24">
              <button
                type="button"
                onClick={() => setLeaveStartTimePeriod('AM')}
          className={`flex-1 py-1 px-2 text-xs font-medium rounded-full transition-[background-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 ${
                  leaveStartTimePeriod === 'AM'
                    ? 'bg-blue-800 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                AM
              </button>
              <button
                type="button"
                onClick={() => setLeaveStartTimePeriod('PM')}
          className={`flex-1 py-1 px-2 text-xs font-medium rounded-full transition-[background-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 ${
                  leaveStartTimePeriod === 'PM'
                    ? 'bg-blue-800 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                PM
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* END DATE (non-Home Leave) */}
      {!isHomeLeave && (
      <div className="form-group">
        <label className="form-label">
          {isUrgentLeave ? 'End Date (Auto-calculated)' : 'End Date *'}
        </label>
        {isUrgentLeave ? (
          // Urgent Leave: Read-only auto-calculated (14 calendar days)
          <>
            <DatePickerField
              theme="employee"
              selected={parseLocalDate(leaveEnd)}
              onChange={() => {}}
              disabled
              placeholderText="Auto-calculated"
            />
            {isCalculating && (
              <p className="text-xs text-gray-600 mt-1">Calculating 14 calendar days...</p>
            )}
            {calculatedEndDate && !isCalculating && (
              <p className="text-xs text-gray-600 mt-1">
                14 calendar days from {formatDate(leaveStart)}
              </p>
            )}
          </>
        ) : (
          // Annual Leave: Manual selection with AM/PM
          <div className="flex gap-2">
            <div className="flex-1">
              <DatePickerField
                theme="employee"
                selected={parseLocalDate(leaveEnd)}
                onChange={(date) => setLeaveEnd(date ? toLocalISODate(date) : '')}
                minDate={parseLocalDate(leaveStart)}
                placeholderText="Select end date"
              />
            </div>
            <div className="flex bg-gray-100 rounded-full p-1 w-24">
              <button
                type="button"
                onClick={() => setLeaveEndTimePeriod('AM')}
          className={`flex-1 py-1 px-2 text-xs font-medium rounded-full transition-[background-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 ${
                  leaveEndTimePeriod === 'AM'
                    ? 'bg-blue-800 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                AM
              </button>
              <button
                type="button"
                onClick={() => setLeaveEndTimePeriod('PM')}
          className={`flex-1 py-1 px-2 text-xs font-medium rounded-full transition-[background-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 ${
                  leaveEndTimePeriod === 'PM'
                    ? 'bg-blue-800 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                PM
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      <ContinuousHomeLeaveBlocker policy={conflictCheck.continuousHomeLeave} />

      {/* OVERLAP CLASH: shown for all leave types — blocks submit, mirrors the backend overlap guard */}
      {conflictCheck.hasConflicts && !continuousHomeLeaveBlocked && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-orange-800 mb-2">Leave Clash Detected</h4>
              <p className="text-sm text-orange-700 mb-3">
                Your selected dates overlap with leave you already have:
              </p>
              <div className="space-y-2">
                {conflictCheck.conflicts.map((conflict) => (
                  <div key={conflict.id} className="bg-white rounded border border-orange-200 p-3">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-orange-700">
                      <span className="font-medium text-orange-900">{conflict.leave_type}</span>
                      <span>{formatDate(conflict.start_date)} → {formatDate(conflict.end_date)}</span>
                      <StatusBadge status={conflict.status} />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-sm text-orange-700">
                <strong>Action needed:</strong> pick dates that don't overlap the leave above to continue.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ANNUAL LEAVE: Show calculated days with OIL consumption preview */}
      {isAnnualLeave && leaveStart && leaveEnd && (
        <div className="space-y-3">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-blue-900">Total Days</span>
              {calcLoading ? (
                <span className="text-sm text-blue-700">Calculating…</span>
              ) : (
                <span className="text-base font-semibold text-gray-900">
                  {formatLeaveDays(calculatedLeaveDays)} {pluralizeDays(calculatedLeaveDays)}
                  {proposalDisplay && (
                    <span className="text-sm font-medium text-blue-700 ml-2">
                      ({proposalDisplay})
                    </span>
                  )}
                </span>
              )}
            </div>
            <p className="text-sm text-blue-800 mt-1">
              Charged against working days on your roster.
            </p>
            {proposalDisplay && (
              <p className="text-sm text-teal-700 mt-2 font-medium">
                Off in Lieu balance will be used first
              </p>
            )}
          </div>
          {futureYearProjection?.supported && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start gap-3">
                <Info
                  className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-blue-900">
                    Using your projected {futureYearProjection.balance_year} balance
                  </p>
                  <p className="text-sm text-blue-800 mt-1">
                    Applying before 1 January does not reduce your {futureYearProjection.source_year} balance.
                  </p>
                </div>
              </div>
              <div className="mt-3 space-y-1 text-sm text-blue-800">
                <p>
                  {formatLeaveDays(futureYearProjection.projected_carry_forward_days)} days expected from {futureYearProjection.source_year} carry-forward
                  {' · '}
                  {formatLeaveDays(futureYearProjection.projected_earned_days)} days earned by your leave date
                </p>
                {Number(futureYearProjection.reserved_days_before_request || 0) > 0 && (
                  <p>
                    {formatLeaveDays(futureYearProjection.reserved_days_before_request)} days are already reserved for other {futureYearProjection.balance_year} leave.
                  </p>
                )}
                <p className="font-medium text-blue-900">
                  {formatLeaveDays(futureYearProjection.annual_leave_days_in_request)} Annual Leave days reserved
                  {' · '}
                  {formatLeaveDays(futureYearProjection.projected_remaining_after_request)} days projected remaining
                </p>
                {Number(futureYearProjection.projected_forfeiture_days || 0) > 0 && (
                  <p className="font-medium text-blue-900">
                    Your {futureYearProjection.source_year} balance is {formatLeaveDays(futureYearProjection.projected_forfeiture_days)} {pluralizeDays(futureYearProjection.projected_forfeiture_days)} above the carry-forward limit. This excess must be used by 31 December {futureYearProjection.source_year}. It will not carry into {futureYearProjection.balance_year} and cannot be used for leave dates from 1 January {futureYearProjection.balance_year}.
                  </p>
                )}
              </div>
            </div>
          )}
          {futureYearProjection && !futureYearProjection.supported && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle
                  className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-amber-900">
                    Future paid balance is not available
                  </p>
                  <p className="text-sm text-amber-800 mt-1">
                    {futureYearProjection.reason || 'Choose dates in the next calendar year or contact HR.'}
                  </p>
                </div>
              </div>
            </div>
          )}
          {unpaidDays > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle
                  className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-amber-900">
                    Includes Unpaid Leave
                  </p>
                  <p className="text-sm text-amber-800 mt-1">
                    {formatLeaveDays(unpaidDays)} {pluralizeDays(unpaidDays)} will be unpaid because the projected paid balance is insufficient.
                  </p>
                </div>
              </div>
            </div>
          )}
          <LeaveExcludedDaysCallout
            excludedDates={excludedLeaveDates}
            leaveTypeName="Annual Leave"
            audience="employee"
          />
        </div>
      )}

      {/* ANNUAL LEAVE: Reason dropdown */}
      {isAnnualLeave && (
        <div className="form-group">
          <label className="form-label">Reason for Leave *</label>
          <FormSelect
            value={leaveReason}
            onChange={(val) => {
              setLeaveReason(val);
              if (val !== 'other') {
                setOtherReason('');
              }
            }}
            options={ANNUAL_LEAVE_REASONS}
            theme="employee"
            required
          />
          
          {/* Show text input when "Other" is selected */}
          {leaveReason === 'other' && (
            <div className="mt-3">
              <label className="form-label">Please specify *</label>
              <input
                type="text"
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Enter your reason..."
                className="input"
                required
              />
            </div>
          )}
        </div>
      )}

      {/* URGENT LEAVE: Reason dropdown (mandatory) */}
      {isUrgentLeave && (
        <div className="form-group">
          <label className="form-label">Reason for Urgent Leave *</label>
          <FormSelect
            value={leaveReason}
            onChange={(val) => {
              setLeaveReason(val);
              if (val !== 'other') {
                setOtherReason('');
              }
            }}
            options={URGENT_LEAVE_REASONS}
            theme="employee"
            required
          />
          
          {leaveReason === 'other' && (
            <div className="mt-3">
              <label className="form-label">Please specify *</label>
              <input
                type="text"
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Enter your reason..."
                className="input"
                required
              />
            </div>
          )}
        </div>
      )}

      {/* HOME LEAVE: AL consumption preview is now integrated into the date section above */}

      {/* URGENT LEAVE: Show AL → Unpaid Leave consumption preview */}
      {isUrgentLeave && alPreview && (() => {
        const alDays = parseFloat(alPreview.annual_leave_to_consume) || 0;
        const ulDays = parseFloat(alPreview.unpaid_leave_to_consume) || 0;
        const chips = [];
        if (alDays > 0) chips.push({ days: alDays, label: 'AL', type: 'Annual Leave' });
        if (ulDays > 0) chips.push({ days: ulDays, label: 'UL', type: 'Unpaid Leave' });

        if (chips.length === 0) return null;

        return (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Leave Breakdown</span>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-800">
                14 calendar days
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-2">
              {chips.map((chip, idx) => (
                <React.Fragment key={chip.label}>
                  {idx > 0 && <span className="text-gray-400 text-xs">+</span>}
                  <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ring-1 ring-inset ${getLeaveTypeBadgeClass(chip.type)}`}>
                    {formatLeaveDays(chip.days)} {chip.label}
                  </span>
                </React.Fragment>
              ))}
            </div>
            {ulDays > 0 && (
              <p className="text-xs text-orange-700 mt-2">
                Annual Leave balance consumed first. Remainder is Unpaid Leave.
              </p>
            )}
          </div>
        );
      })()}

    </div>
  );
};

const ApplicationSummary = ({ leave, leaveType, onBack }) => {
  if (!leave) return null;

  const isHomeLeave = leaveType === 'home_leave';
  const isAnnualLeave = leaveType === 'annual_leave';
  const isUrgentLeave = leaveType === 'urgent_leave';

  const typeName = leave.leave_type?.leave_type_name
    || (isHomeLeave ? 'Home Leave' : isUrgentLeave ? 'Urgent Leave' : 'Annual Leave');

  const totalDays = leave.leave_total_days;
  const isHalfDay = isAnnualLeave && Number(totalDays) < 1;

  // The breakdown is only informative when leave is split across multiple types
  // (e.g. OIL + AL, AL + UL). A single item equal to the parent type just repeats
  // the Type and Duration fields above it.
  const breakdownItems = leave.leave_consumption_proposal?.items || [];
  const showBreakdown = breakdownItems.length > 1
    || (breakdownItems.length === 1 && breakdownItems[0].leave_type !== typeName);

  return (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2 text-sm text-blue-800">
        <FileText className="w-4 h-4 flex-shrink-0" />
        <span>Read-only summary of your submitted application.</span>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Application Details</div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="field-label">Type</span>
            <div className="field-display">
              <LeaveTypeBadge leaveTypeName={typeName} />
            </div>
          </div>
          <div>
            <span className="field-label">Duration</span>
            <div className="field-display">
              {formatLeaveDays(totalDays)} {pluralizeDays(totalDays)}
              {isHalfDay && (
                <SessionPeriodChip session={leave.leave_start_time_period === 'PM' ? 'PM' : null} className="ml-1.5" />
              )}
              {isHalfDay && (
                <SessionPeriodChip session={leave.leave_end_time_period === 'AM' ? 'AM' : null} className="ml-1.5" />
              )}
            </div>
          </div>
          <div>
            <span className="field-label">Start Date</span>
            <div className="field-display">
              {formatDate(leave.leave_start_date)}
              {!isHalfDay && (
                <SessionPeriodChip session={leave.leave_start_time_period === 'PM' ? 'PM' : null} className="ml-1.5" />
              )}
            </div>
          </div>
          <div>
            <span className="field-label">End Date</span>
            <div className="field-display">
              {formatDate(leave.leave_end_date)}
              {!isHalfDay && (
                <SessionPeriodChip session={leave.leave_end_time_period === 'AM' ? 'AM' : null} className="ml-1.5" />
              )}
            </div>
          </div>
        </div>

        {leave.leave_reason && (
          <div className="pt-2 border-t border-gray-200">
            <span className="field-label">Reason</span>
            <div className="field-display">{leave.leave_reason}</div>
          </div>
        )}

        {showBreakdown && (
          <div className="pt-2 border-t border-gray-200">
            <span className="field-label">Leave Breakdown</span>
            <div className="space-y-1 mt-1">
              {breakdownItems.map((item, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <LeaveTypeBadge leaveTypeName={item.leave_type} size="xs" />
                  <span className="font-medium">{formatLeaveDays(item.days)} {pluralizeDays(item.days)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {leave.leave_applied_at && (
          <div className="pt-2 border-t border-gray-200">
            <span className="field-label">Submitted</span>
            <div className="field-display">{formatDateTimeCompact(leave.leave_applied_at)}</div>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onBack}
        className="btn btn-outline w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
      >
        <ChevronRight className="w-4 h-4 mr-1.5 rotate-180" />
        Back to Current Step
      </button>
    </div>
  );
};

const ROLE_LABELS = {
  reporting_officer: 'Reporting Officer',
  trade_leader: 'Trade Leader',
  deputy_hod: 'Deputy HOD',
  department_head: 'Department Head',
  line_manager: 'Line Manager',
  hr_manager: 'HR Manager',
  finance_manager: 'Finance Manager',
};

const Step2ApprovalProgress = ({ leave, leaveType }) => {
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-SG', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    return `${formatDate(ts)} ${formatTime(ts)}`;
  };

  const levels = (leave?.approval_hierarchy && leave.approval_hierarchy.length > 0)
    ? leave.approval_hierarchy.map(h => ({
        level: h.level,
        role: ROLE_LABELS[h.role] || h.role,
        approverName: h.approver_name || '',
        approverStaffId: h.approver_staff_id || '',
      }))
    : [
        { level: 1, role: 'Reporting Officer', approverName: '', approverStaffId: '' },
        { level: 2, role: 'Trade Leader', approverName: '', approverStaffId: '' },
      ];

  const isHomeLeave = leaveType === 'home_leave';
  const isUrgentLeave = leaveType === 'urgent_leave';
  const history = leave?.approval_history || [];

  return (
    <div className="space-y-4">
      {/* At-a-glance horizontal position in the approval chain */}
      <ApprovalStepper leave={leave} />

      {levels.map(({ level, role, approverName, approverStaffId }) => {
        const approvalEntry = history.find(h => h.level === level && (h.action === 'approved' || h.action === 'auto_skipped'));
        const autoAdvancedEntry = history.find(h => h.level === level && h.action === 'auto_advanced');
        const escalationEntry = history.find(h => h.level === level && h.action === 'escalated');
        const rejectionEntry = history.find(h => h.level === level && h.action === 'rejected');
        const isCurrent = leave?.current_approval_level === level;

        const levelLabel = approverName
          ? `Level ${level}: ${role} — ${approverName}`
          : `Level ${level}: ${role}`;

        let iconBg, icon, detail;

        if (approvalEntry) {
          iconBg = 'bg-green-100';
          icon = <CheckCircle className="w-5 h-5 text-green-600" />;
          const ts = formatTimestamp(approvalEntry.approved_at);
          if (approvalEntry.action === 'auto_skipped') {
            const skipReason = (approvalEntry.reason || '').trim();
            const isSameApprover = /same approver/i.test(skipReason);
            const skipLabel = isSameApprover || approvalEntry.approver_id
              ? 'Auto-approved (same approver as previous level)'
              : (skipReason || 'Skipped — level not required');
            detail = <div className="text-xs text-gray-600">{skipLabel}{ts && ` • ${ts}`}</div>;
          } else {
            const who = [approvalEntry.approver_name, approvalEntry.approver_id].filter(Boolean).join(' • ') || '';
            detail = <div className="text-xs text-gray-600">{who ? `Approved by: ${who}` : 'Approved'}{ts && ` • ${ts}`}</div>;
          }
        } else if (rejectionEntry) {
          iconBg = 'bg-red-100';
          icon = <XCircle className="w-5 h-5 text-red-600" />;
          const who = [rejectionEntry.approver_name, rejectionEntry.approver_id].filter(Boolean).join(' • ') || '';
          const ts = formatTimestamp(rejectionEntry.approved_at);
          detail = (
            <div className="text-xs text-red-700">
              {who ? `Rejected by: ${who}` : 'Rejected'}{ts && ` • ${ts}`}
              {rejectionEntry.reason && <span className="block mt-0.5 text-red-600">Reason: {rejectionEntry.reason}</span>}
            </div>
          );
        } else if (autoAdvancedEntry) {
          iconBg = 'bg-gray-100';
          icon = <ArrowRight className="w-5 h-5 text-gray-500" />;
          const originalWho = [autoAdvancedEntry.original_approver_name, autoAdvancedEntry.original_approver_id].filter(Boolean).join(' • ');
          detail = (
            <div className="text-xs text-gray-500">
              <span>No response — advanced to next level</span>
              {originalWho && <span className="block mt-0.5">{originalWho}</span>}
            </div>
          );
        } else if (escalationEntry && isCurrent) {
          iconBg = 'bg-amber-100';
          icon = <AlertTriangle className="w-5 h-5 text-amber-600" />;
          const originalWho = [escalationEntry.original_approver_name, escalationEntry.original_approver_id].filter(Boolean).join(' • ');
          const escalatedToName = escalationEntry.escalation_approver_name || leave?.current_approver?.name || '';
          const escalatedToRole = ROLE_LABELS[escalationEntry.escalation_role] || escalationEntry.escalation_role || '';
          const escalatedToLabel = [escalatedToName, escalatedToRole].filter(Boolean).join(' (') + (escalatedToName && escalatedToRole ? ')' : '');
          detail = (
            <div className="text-xs text-amber-800">
              <span>Escalated to {escalatedToLabel || 'next approver'}</span>
              {originalWho && <span className="block mt-0.5 text-amber-600">No response from {originalWho}</span>}
            </div>
          );
        } else if (isCurrent) {
          iconBg = 'bg-blue-100';
          icon = <Clock className="w-5 h-5 text-blue-600" />;
          detail = <div className="text-xs text-blue-700">Pending with: {leave?.current_approver?.name || approverName || 'Approver'}</div>;
        } else {
          iconBg = 'bg-gray-100';
          icon = <Clock className="w-5 h-5 text-gray-400" />;
          detail = <div className="text-xs text-gray-500">{approverName ? `Assigned to: ${approverName}` : 'Awaiting previous approval'}</div>;
        }

        return (
          <div key={level} className="flex items-start border rounded-lg p-3">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${iconBg}`}>
              {icon}
            </div>
            <div className="ml-3 flex-1">
              <div className="text-sm font-semibold">{levelLabel}</div>
              {detail}
            </div>
          </div>
        );
      })}
      <div className={`${isUrgentLeave ? 'bg-red-50 border-red-200 text-red-800' : 'bg-blue-50 border-blue-200 text-blue-800'} border rounded-lg p-3 text-xs`}>
        {(isHomeLeave || isUrgentLeave)
          ? 'You will be notified when all approvals are complete. You can then submit your flight details.'
          : 'You will be notified when all approvals are complete.'}
      </div>
    </div>
  );
};

const Step3FlightDetails = ({ leave, onSubmitted, onClose, leaveType, previousRejection, uploadStateRef, onDocActiveChange, steps, currentStep, onStepClick }) => {
  const isUrgentLeave = leaveType === 'urgent_leave';
  const isHomeLeave = leaveType === 'home_leave';
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('document');

  // ── Pattern B state machine ──────────────────────────────────────────────
  // idle → uploading → processing_ocr → ocr_complete | ocr_failed
  const [phase, setPhase] = useState('idle');
  const [file, setFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [fileType, setFileType] = useState(null); // 'image' | 'pdf'
  const [docApprovalId, setDocApprovalId] = useState(null);
  const [ocrData, setOcrData] = useState(null);

  // Confirmed form fields (prefilled from OCR, editable by employee)
  const [dep, setDep] = useState('');
  const [ret, setRet] = useState('');
  const [bookingRef, setBookingRef] = useState('');
  const [depAirport, setDepAirport] = useState('');
  const [arrAirport, setArrAirport] = useState('');
  const [depFlightNum, setDepFlightNum] = useState('');
  const [arrFlightNum, setArrFlightNum] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hardErrors, setHardErrors] = useState([]);
  const [softMismatches, setSoftMismatches] = useState([]);
  const isOneWayOutbound = isOneWayOutboundTrip(ocrData?.trip_type);
  const requiresReturn = isHomeLeave && !isOneWayOutbound;

  const viewer = useImageViewer();
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollingRef = useRef(false);

  // ── Sync upload state to parent ref for close-guard ────────────────────
  useEffect(() => {
    if (uploadStateRef) {
      const hasUpload = phase !== 'idle' && !submitting;
      uploadStateRef.current = hasUpload
        ? { leaveId: leave?.id, docApprovalId }
        : null;
    }
  }, [phase, docApprovalId, leave?.id, submitting, uploadStateRef]);

  // ── Tell the parent when to expand into the split-screen workspace ──────
  // Compact upload prompt during idle/uploading; the wide document+form
  // layout is only needed once a preview/OCR result is on screen.
  useEffect(() => {
    onDocActiveChange?.(phase !== 'idle' && phase !== 'uploading');
    return () => onDocActiveChange?.(false);
  }, [phase, onDocActiveChange]);

  // ── Detect file type from MIME or extension ──────────────────────────────
  const detectFileType = useCallback((f) => {
    if (!f) return null;
    const mime = f.type || '';
    if (mime.startsWith('image/')) return 'image';
    if (mime === 'application/pdf') return 'pdf';
    const name = (f.name || '').toLowerCase();
    if (/\.(jpg|jpeg|png|gif|webp)$/.test(name)) return 'image';
    if (name.endsWith('.pdf')) return 'pdf';
    return 'image';
  }, []);

  // ── Resume: only for active OCR states, not 'pending' (pending = upload
  //    happened but OCR wasn't triggered or failed — user should re-upload).
  useEffect(() => {
    const verif = leave?.flight_ticket_verification;
    if (!verif) return;

    const daId = verif.document_approval_id;
    const daStatus = verif.status;

    if (!['processing_ocr', 'ocr_completed'].includes(daStatus)) return;

    setDocApprovalId(daId);

    if (daStatus === 'ocr_completed') {
      staffAPI.getFlightTicketOCRStatus(leave.id).then(resp => {
        if (resp.data?.ocr_data) {
          setOcrData(resp.data.ocr_data);
          setPhase('ocr_complete');
        } else {
          setPhase('ocr_failed');
        }
      }).catch(() => setPhase('ocr_failed'));
    } else {
      setPhase('processing_ocr');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Prefill form when OCR data arrives ───────────────────────────────────
  useEffect(() => {
    if (!ocrData) return;
    if (ocrData.departure_date) setDep(ocrData.departure_date);
    if (ocrData.return_date) setRet(ocrData.return_date);
    if (ocrData.booking_reference) setBookingRef(ocrData.booking_reference);
    if (ocrData.departure_airport) setDepAirport(ocrData.departure_airport);
    if (ocrData.arrival_airport) setArrAirport(ocrData.arrival_airport);
    if (ocrData.departure_flight_number) setDepFlightNum(ocrData.departure_flight_number);
    if (ocrData.arrival_flight_number) setArrFlightNum(ocrData.arrival_flight_number);
  }, [ocrData]);

  // A one-way outbound ticket has no return leg. Clear any stale values that
  // may have been entered before OCR completed so they cannot be submitted.
  useEffect(() => {
    if (isUrgentLeave || isOneWayOutbound) {
      setRet('');
      setArrFlightNum('');
    }
  }, [isUrgentLeave, isOneWayOutbound]);

  // ── Date validation ──────────────────────────────────────────────────────
  useEffect(() => {
    const hard = [];
    const soft = [];
    if (isUrgentLeave) {
      if (dep && leave?.leave_start_date && dep !== leave.leave_start_date) {
        soft.push({ field: 'Departure date', ticket: dep, approved: leave.leave_start_date });
      }
    } else if (isHomeLeave) {
      if (dep && leave?.leave_start_date && dep !== leave.leave_start_date) {
        soft.push({ field: 'Departure date', ticket: dep, approved: leave.leave_start_date });
      }
      if (ret && leave?.leave_end_date && ret !== leave.leave_end_date) {
        soft.push({ field: 'Return date', ticket: ret, approved: leave.leave_end_date });
      }
    }
    setHardErrors(hard);
    setSoftMismatches(soft);
  }, [dep, ret, leave, isUrgentLeave, isHomeLeave]);

  // ── OCR polling (DS: spinner for modal content, 300ms rule) ──────────────
  const startOCRPolling = useCallback((leaveId) => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    let attempt = 0;
    const maxAttempts = 6;
    const intervalMs = 5000;

    const poll = async () => {
      while (pollingRef.current && attempt < maxAttempts) {
        await new Promise(r => setTimeout(r, intervalMs));
        if (!pollingRef.current) break;
        attempt++;
        try {
          const resp = await staffAPI.getFlightTicketOCRStatus(leaveId);
          if (resp.data?.status === 'ocr_completed' && resp.data?.ocr_data) {
            setOcrData(resp.data.ocr_data);
            setPhase('ocr_complete');
            pollingRef.current = false;
            return;
          }
        } catch { /* retry */ }
      }
      if (pollingRef.current) {
        setPhase('ocr_failed');
        pollingRef.current = false;
      }
    };
    poll();
  }, []);

  useEffect(() => {
    if (phase === 'processing_ocr' && leave?.id && !pollingRef.current) {
      startOCRPolling(leave.id);
    }
    return () => { pollingRef.current = false; };
  }, [phase, leave?.id, startOCRPolling]);

  // ── Cleanup blob URLs ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    };
  }, [filePreviewUrl]);

  // ── File selection (dropzone or camera) ──────────────────────────────────
  const handleFileSelect = useCallback(async (rawFile) => {
    if (!rawFile) return;

    const selectedFile = await normalizeHeicFileForUpload(rawFile);

    const maxSize = 5 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      toast.error('File size exceeds 5 MB limit');
      return;
    }

    setFile(selectedFile);
    setFileType(detectFileType(selectedFile));
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    setFilePreviewUrl(URL.createObjectURL(selectedFile));
    viewer.reset();
    setPhase('uploading');
    setOcrData(null);
    setDep('');
    setRet('');
    setBookingRef('');
    setDepAirport('');
    setArrAirport('');

    try {
      const uploadResp = await staffAPI.uploadFlightTicket(leave.id, selectedFile);
      const daId = uploadResp.data?.document_approval_id;
      const staffId = uploadResp.data?.staff_id || leave?.leave_staff_id_id || leave?.staff_id || '';
      setDocApprovalId(daId);

      setPhase('processing_ocr');

      if (daId) {
        try {
          const ocrResp = await staffAPI.sendFlightTicketToN8N(daId, selectedFile, staffId, leave.id);
          const payload = ocrResp?.data?.data || ocrResp?.data || {};
          const item = Array.isArray(payload) ? payload[0] : payload;
          const extracted = item?.ocr_data || item?.extracted_data || item;

          if (extracted && (extracted.departure_date || extracted.passenger_name || extracted.departure_airport)) {
            setOcrData({
              passenger_name: extracted.passenger_name || extracted.ocr_passenger_name || '',
              booking_reference: extracted.booking_reference || extracted.ocr_booking_reference || '',
              departure_date: extracted.departure_date || extracted.ocr_departure_date || null,
              return_date: extracted.return_date || extracted.ocr_return_date || null,
              departure_airport: extracted.departure_airport || extracted.ocr_departure_airport || '',
              arrival_airport: extracted.arrival_airport || extracted.ocr_arrival_airport || '',
              departure_flight_number: extracted.departure_flight_number || extracted.ocr_departure_flight_number || '',
              arrival_flight_number: extracted.arrival_flight_number || extracted.ocr_arrival_flight_number || '',
              trip_type: extracted.trip_type || extracted.ocr_trip_type || '',
            });
            setPhase('ocr_complete');
            return;
          }
        } catch { /* n8n call failed — fall through to polling */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Upload failed. Please try again.');
      setPhase('idle');
    }
  }, [leave, filePreviewUrl, detectFileType, viewer.reset]);

  const handleCameraCapture = useCallback((e) => {
    const f = e.target.files?.[0];
    if (f) handleFileSelect(f);
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  }, [handleFileSelect]);

  // ── Re-upload (reset to idle) ────────────────────────────────────────────
  const handleReUpload = useCallback(() => {
    setPhase('idle');
    setFile(null);
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    setFilePreviewUrl(null);
    setFileType(null);
    setOcrData(null);
    setDep('');
    setRet('');
    setBookingRef('');
    setDepAirport('');
    setArrAirport('');
    setDepFlightNum('');
    setArrFlightNum('');
    pollingRef.current = false;
  }, [filePreviewUrl]);

  // ── Submit confirmed fields (Pattern B stage 2) ──────────────────────────
  const handleSubmit = async () => {
    if (!dep) {
      toast.error('Departure date is required');
      return;
    }
    if (requiresReturn && !ret) {
      toast.error('Return date is required');
      return;
    }
    if (hardErrors.length > 0) {
      toast.error('Please fix the date errors before submitting');
      return;
    }

    const form = new FormData();
    form.append('flight_departure_date', dep);
    if (requiresReturn && ret) form.append('flight_return_date', ret);
    if (bookingRef) form.append('flight_booking_reference', bookingRef);
    if (depAirport) form.append('flight_departure_airport', depAirport);
    if (arrAirport) form.append('flight_arrival_airport', arrAirport);
    if (depFlightNum) form.append('flight_departure_flight_number', depFlightNum);
    if (requiresReturn && arrFlightNum) form.append('flight_arrival_flight_number', arrFlightNum);

    setSubmitting(true);
    try {
      await staffAPI.submitFlightDetails(leave.id, form);
      toast.success('Flight ticket submitted — HR will verify your ticket shortly.');
      onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isIdle = phase === 'idle';
  const hasPreview = !!filePreviewUrl && !isIdle;

  /*
   * Split shell — design-system-reference.md § Split-Screen Verification Modal Pattern:
   * - useIsMobile (max-width 767px) — do not use ad-hoc resize listeners
   * - Desktop: w-2/3 document | w-1/3 form, side-by-side
   * - Mobile: MobileVerificationTabs (tab-btn) — full-width Ticket | Details panels
   * - Panel visibility via hidden class (preserves zoom/pan/scroll state)
   */
  const formReady = phase === 'ocr_complete' || phase === 'ocr_failed';
  const documentPanelVisible = !isMobile || activeTab === 'document';
  const formPanelVisible = formReady && (!isMobile || activeTab === 'form');

  // ── Document panel content ───────────────────────────────────────────────
  const renderDocumentPanel = () => {
    if (isIdle || phase === 'uploading') {
      return (
        <div className="p-6">
          <div className="max-w-xl mx-auto">
          <div className="flex flex-col items-center text-center mb-5">
            <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-3">
              <Plane className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-sm font-medium text-gray-800">Add your flight ticket</p>
            <p className="text-xs text-gray-500 mt-1">Upload your e-ticket or a clear photo (PDF, JPG, PNG · max 5 MB).</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) handleFileSelect(f);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={phase === 'uploading'}
                className="w-full bg-white hover:bg-gray-50 text-gray-700 rounded-xl py-4 px-3 border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all flex flex-col items-center gap-2 disabled:opacity-50 disabled:pointer-events-none"
              >
                <Upload className="w-6 h-6 text-blue-600" />
                <span className="font-medium text-sm">Choose File</span>
              </button>
            </div>

            <div>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCameraCapture}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={phase === 'uploading'}
                className="w-full bg-white hover:bg-gray-50 text-gray-700 rounded-xl py-4 px-3 border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all flex flex-col items-center gap-2 disabled:opacity-50 disabled:pointer-events-none"
              >
                <Smartphone className="w-6 h-6 text-blue-600" />
                <span className="font-medium text-sm">Use Camera</span>
              </button>
            </div>
          </div>

          {phase === 'uploading' && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-2">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mt-0.5 flex-shrink-0"></div>
                <div>
                  <p className="text-sm font-medium text-blue-800">Uploading & reading your ticket...</p>
                  <p className="text-xs text-blue-600 mt-1">Please keep this modal open.</p>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      );
    }

    // No preview available yet — show spinner while loading
    if (!filePreviewUrl) {
      return (
        <div className="flex flex-col items-center justify-center h-full py-16">
          <LoadingSpinner size="md" />
          <p className="text-sm text-gray-500 mt-3">Loading document…</p>
        </div>
      );
    }

    // Document preview (image zoom/pan or PDF iframe)
    return (
      <div className="relative w-full h-full" style={isMobile ? undefined : { minHeight: '600px' }}>
        {fileType === 'image' ? (
          <div
            ref={viewer.containerRef}
            {...viewer.panHandlers}
            className="w-full h-full overflow-hidden select-none"
            style={viewer.containerStyle}
          >
            <img
              src={filePreviewUrl}
              alt="Flight Ticket"
              className="max-w-none"
              draggable={false}
              style={{
                transform: viewer.imageTransform,
                transformOrigin: '0 0',
                transition: viewer.isPanning ? 'none' : 'transform 0.1s ease-out',
              }}
            />
          </div>
        ) : (
          <PdfViewer
            src={filePreviewUrl}
            title="Document"
            fileName="document.pdf"
            theme="employee"
          />
        )}

        {/* DS: Floating zoom controls — image viewer only, overlay inside viewer */}
        {fileType === 'image' && (
          <div
            className="absolute bottom-3 right-3 z-10 flex items-center gap-0.5 bg-white/95 border border-gray-200 rounded-full shadow-md p-1"
            onMouseDown={e => e.stopPropagation()}
          >
            <button type="button" onClick={viewer.zoomOut} className="icon-button" title="Zoom out" aria-label="Zoom out">
              <ZoomOut className="w-4 h-4" />
            </button>
            <button type="button" onClick={viewer.zoomIn} className="icon-button" title="Zoom in" aria-label="Zoom in">
              <ZoomIn className="w-4 h-4" />
            </button>
            <button type="button" onClick={viewer.reset} className="icon-button" title="Fit to panel" aria-label="Fit to panel">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── Form panel content ───────────────────────────────────────────────────
  const renderFormPanel = () => {
    if (isIdle) {
      return (
        <div className="px-6 py-6">
          <div className="flex flex-col items-center text-center">
            <Plane className="w-10 h-10 text-gray-300 mb-3" />
            <p className="text-sm text-gray-500 font-medium">Upload your ticket to get started</p>
            <p className="text-xs text-gray-400 mt-1 max-w-[220px]">The OCR will read your ticket and prefill the details for you.</p>
          </div>
        </div>
      );
    }

    if (phase === 'uploading' || phase === 'processing_ocr') {
      return (
        <div className="px-6 py-6">
          <div className="flex flex-col items-center text-center">
            <LoadingSpinner size="lg" />
            <p className="text-sm text-blue-700 font-medium mt-4">Reading your ticket...</p>
            <p className="text-xs text-gray-500 mt-1">This usually takes 10-20 seconds.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {phase === 'ocr_failed' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-800">
            <span className="font-medium">Could not read ticket automatically.</span> Please fill in the details manually.
          </div>
        )}

        {/* Status chips stay a chip row; the instruction is a banner, not a third chip */}
        <div className="flex items-center gap-2 flex-wrap">
          <InfoBadge
            color="blue"
            label={
              isOneWayOutbound
                ? 'Return not required'
                : isHomeLeave
                  ? 'Required: Return'
                  : 'Required: One Way Out'
            }
          />
          {ocrData?.trip_type && (
            <InfoBadge
              color={
                (
                  (
                    isHomeLeave
                    && ![
                      'round_trip',
                      'return',
                      'one_way_outbound',
                      'one_way_out',
                    ].includes(ocrData.trip_type)
                  )
                  || (isUrgentLeave && ['round_trip', 'return'].includes(ocrData.trip_type))
                )
                  ? 'amber'
                  : isOneWayOutbound ? 'blue' : 'gray'
              }
              label={`OCR: ${FLIGHT_TRIP_TYPE_LABELS[ocrData.trip_type] || 'Unknown'}`}
            />
          )}
        </div>

        {isOneWayOutbound && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <p className="text-xs text-blue-800">
                <span className="font-semibold">One-way outbound ticket.</span>
                {' '}Only the departure details need to be confirmed. Your approved leave end date remains unchanged.
              </p>
            </div>
          </div>
        )}

        {ocrData?.trip_type && (
          (
            isHomeLeave
            && ![
              'round_trip',
              'return',
              'one_way_outbound',
              'one_way_out',
              'unknown',
            ].includes(ocrData.trip_type)
          ) ||
          (isUrgentLeave && ['round_trip', 'return'].includes(ocrData.trip_type))
        ) && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                {isHomeLeave
                  ? 'Ticket type could not be matched to the expected Home Leave journey'
                  : 'Ticket shows a return leg — only the outbound is needed'}
              </p>
            </div>
          </div>
        )}

        {ocrData?.passenger_name && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="flex-shrink-0">Passenger:</span>
            <span className="font-medium text-gray-800 truncate uppercase">{ocrData.passenger_name}</span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          <div className="form-group">
            <label className="form-label">
              Departure Date *
              {dep && isHomeLeave && dep !== leave?.leave_start_date && (
                <span className="ml-2 text-xs text-amber-600 font-medium">⚠ Mismatch</span>
              )}
            </label>
            <DatePickerField
              theme="employee"
              selected={parseLocalDate(dep)}
              minDate={isUrgentLeave ? parseLocalDate(leave?.leave_start_date) : undefined}
              onChange={(date) => setDep(date ? toLocalISODate(date) : '')}
              placeholderText="Select departure date"
            />
          </div>
          {requiresReturn && (
            <div className="form-group">
              <label className="form-label">
                Return Date *
                {ret && isHomeLeave && ret !== leave?.leave_end_date && (
                  <span className="ml-2 text-xs text-amber-600 font-medium">⚠ Mismatch</span>
                )}
              </label>
              <DatePickerField
                theme="employee"
                selected={parseLocalDate(ret)}
                onChange={(date) => setRet(date ? toLocalISODate(date) : '')}
                placeholderText="Select return date"
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div className="form-group">
            <label className="form-label">Departure Airport</label>
            <DatalistCombobox
              value={depAirport}
              onChange={setDepAirport}
              suggestions={AIRPORT_SUGGESTIONS}
              placeholder="e.g. Singapore (SIN)"
              className="input h-11 w-full"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Arrival Airport</label>
            <DatalistCombobox
              value={arrAirport}
              onChange={setArrAirport}
              suggestions={AIRPORT_SUGGESTIONS}
              placeholder="e.g. Tiruchirappalli (TRZ)"
              className="input h-11 w-full"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Booking Reference</label>
          <input type="text" placeholder="e.g. ABC123" value={bookingRef} onChange={e => setBookingRef(e.target.value)} className="input h-11" />
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div className="form-group">
            <label className="form-label">Departure Flight No.</label>
            <input type="text" placeholder="e.g. TR 123" value={depFlightNum} onChange={e => setDepFlightNum(e.target.value)} className="input h-11" />
          </div>
          {requiresReturn && (
            <div className="form-group">
              <label className="form-label">Return Flight No.</label>
              <input type="text" placeholder="e.g. TR 456" value={arrFlightNum} onChange={e => setArrFlightNum(e.target.value)} className="input h-11" />
            </div>
          )}
        </div>

        {hardErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2.5">
            <div className="flex items-start gap-2">
              <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-red-800 space-y-0.5">
                {hardErrors.map((e, i) => <div key={i}>• {e}</div>)}
              </div>
            </div>
          </div>
        )}

        {softMismatches.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            <div className="flex items-start gap-2 min-w-0">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-amber-800 mb-0.5">Date mismatch — HR will review</div>
                {softMismatches.map((m, i) => (
                  <div key={i} className="text-xs text-amber-700 mt-0.5">
                    {m.field}: Ticket <strong>{formatDate(m.ticket)}</strong> · Approved <strong>{formatDate(m.approved)}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {/* Compact top bar: stepper + context info (flex-shrink-0, never scrolls) */}
      <div className="px-6 pt-4 pb-3 space-y-3 flex-shrink-0 border-b border-gray-100">
        {steps && <HorizontalStepper steps={steps} currentStep={currentStep} onStepClick={onStepClick} />}

        <div className="flex items-start gap-2 flex-wrap">
          <div className="border-l-4 border-blue-500 bg-blue-50 px-3 py-2 text-xs flex-1 min-w-0">
            <div className="font-semibold text-blue-900">Approved Leave Period</div>
            <div className="text-blue-800 mt-0.5">
              {formatDate(leave?.leave_start_date)} → {formatDate(leave?.leave_end_date)}
              {' '}· {leave?.leave_total_days} day{Number(leave?.leave_total_days) !== 1 ? 's' : ''}
            </div>
          </div>
          {previousRejection && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2 flex-1 min-w-0">
              <XCircle className="w-3.5 h-3.5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs">
                <div className="font-semibold text-red-800">Previous ticket rejected</div>
                <div className="text-red-700 mt-0.5 line-clamp-2">{previousRejection}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* DS § Split-Screen Verification Modal Pattern — flex-1 fills remaining height */}
      <div className="flex flex-col flex-1 min-h-0 bg-white overflow-hidden">
        <div className={`flex flex-1 min-h-0 ${isMobile ? 'flex-col' : 'flex-row'} overflow-hidden`}>
          {isMobile && formReady && (
            <MobileVerificationTabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
              documentLabel="Ticket"
              formLabel="Details"
              isAdmin={false}
            />
          )}

          {/* Document panel — DS: flex-col min-h-0, scroll body, hidden preserves state */}
          {/* Full-width until OCR completes and form panel appears */}
          <div
            className={`${documentPanelVisible ? (isMobile ? 'flex flex-col min-h-0 overflow-hidden max-h-[min(70vh,520px)]' : `${formReady ? 'w-2/3' : 'w-full'} flex flex-col min-h-0 overflow-hidden transition-all duration-300`) : 'hidden'} ${!isMobile && formReady ? 'border-r border-gray-200' : ''}`}
          >
            <VerificationPanelHeader
              title="Flight Ticket"
              icon={FileText}
              trailing={!isIdle && phase !== 'uploading' ? (
                <button
                  type="button"
                  onClick={handleReUpload}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-full"
                >
                  <Upload className="w-3 h-3" aria-hidden="true" />
                  Re-upload
                </button>
              ) : null}
            />
            {phase === 'processing_ocr' && (
              <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center gap-2 flex-shrink-0">
                <div className="w-3.5 h-3.5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                <p className="text-xs font-medium text-blue-800">Reading your ticket... this usually takes 10-20 seconds.</p>
              </div>
            )}
            <div className="flex-1 min-h-0 overflow-auto overscroll-contain">
              {renderDocumentPanel()}
            </div>
          </div>

          {/* Form panel — DS: scrollable middle + sticky action footer */}
          <div
            className={`${formPanelVisible ? (isMobile ? 'flex flex-col min-h-0 overflow-hidden max-h-[min(70vh,520px)]' : 'w-1/3 flex flex-col min-h-0 min-w-0 overflow-hidden') : 'hidden'}`}
          >
            <VerificationPanelHeader title="Trip Details" />
            <div
              className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-y-contain p-4"
              style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}
            >
              {renderFormPanel()}
            </div>
            {/* DS § Split-Screen Verification — sticky in-panel footer */}
            {formReady && (
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 flex-shrink-0 flex gap-3 btn-row-spread">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || !dep || (requiresReturn && !ret) || hardErrors.length > 0}
                  className="btn btn-primary gap-2"
                >
                  {submitting ? (
                    <>
                      <LoadingSpinner size="sm" color="white" />
                      Submitting…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Step4HRVerification = ({ leave }) => {
  const verif = leave?.flight_ticket_verification;
  const isRejected = verif?.status === 'rejected';

  return (
    <div className="space-y-5">
      {isRejected ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-red-800">Flight Ticket Rejected</div>
            <div className="text-sm text-red-700 mt-1">
              {verif?.rejection_reason || 'Your ticket was rejected. Please re-upload a valid ticket.'}
            </div>
            <div className="text-xs text-red-500 mt-2">
              Go back to Step 3 to re-submit a corrected ticket.
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center space-y-3">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
            <Clock className="w-7 h-7 text-amber-600" />
          </div>
          <div>
            <div className="font-semibold text-amber-800 text-base">Pending HR Verification</div>
            <div className="text-sm text-amber-700 mt-1">
              Your flight ticket has been submitted and is being reviewed by HR.
            </div>
            <div className="text-xs text-amber-600 mt-2">
              Once HR verifies your ticket, your leave balances will be activated and you will be notified.
            </div>
          </div>
        </div>
      )}

      {/* Flight details submitted */}
      {leave?.flight_details_submitted_at && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-xs">
          <div className="font-semibold text-gray-700 uppercase tracking-wide mb-1.5">Submitted Ticket Details</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {leave.flight_departure_date && (
              <div className="flex items-baseline justify-between">
                <span className="text-gray-500">Departure</span>
                <span className="font-medium text-gray-900">{formatDate(leave.flight_departure_date)}</span>
              </div>
            )}
            {leave.flight_return_date && (
              <div className="flex items-baseline justify-between">
                <span className="text-gray-500">Return</span>
                <span className="font-medium text-gray-900">{formatDate(leave.flight_return_date)}</span>
              </div>
            )}
            {(leave.flight_departure_airport || leave.flight_arrival_airport) && (
              <div className="flex items-baseline justify-between">
                <span className="text-gray-500">Route</span>
                <span className="font-medium text-gray-900">{leave.flight_departure_airport || '—'} → {leave.flight_arrival_airport || '—'}</span>
              </div>
            )}
            {leave.flight_booking_reference && (
              <div className="flex items-baseline justify-between">
                <span className="text-gray-500">Booking Ref</span>
                <span className="font-medium font-mono text-gray-900">{leave.flight_booking_reference}</span>
              </div>
            )}
            {leave.flight_departure_flight_number && (
              <div className="flex items-baseline justify-between">
                <span className="text-gray-500">Outbound Flight</span>
                <span className="font-medium text-gray-900">{leave.flight_departure_flight_number}</span>
              </div>
            )}
            {leave.flight_arrival_flight_number && (
              <div className="flex items-baseline justify-between">
                <span className="text-gray-500">Return Flight</span>
                <span className="font-medium text-gray-900">{leave.flight_arrival_flight_number}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Mini-card shown for each extension in the Step5 complete screen ──────────
const ExtensionStatusCard = ({ extension, leaveTypeName, onTrack }) => {
  const totalDays = Number(extension.leave_total_days) || 0;
  const flightSubmitted = !!extension.flight_details_submitted_at;
  const ticketStatus = extension.flight_ticket_verification?.status;
  const flightApproved = ticketStatus === 'approved';
  const flightRejected = ticketStatus === 'rejected';

  let statusLabel, pillClass;
  if (extension.leave_status === 'rejected') {
    statusLabel = 'Rejected'; pillClass = 'bg-red-100 text-red-700';
  } else if (extension.leave_status === 'cancelled') {
    statusLabel = 'Cancelled'; pillClass = 'bg-gray-100 text-gray-600';
  } else if (flightApproved) {
    statusLabel = 'Activated'; pillClass = 'bg-green-100 text-green-700';
  } else if (flightRejected) {
    statusLabel = 'Ticket Rejected'; pillClass = 'bg-red-100 text-red-700';
  } else if (flightSubmitted) {
    statusLabel = 'HR Reviewing'; pillClass = 'bg-blue-100 text-blue-700';
  } else if (extension.leave_status === 'approved' || extension.leave_status === 'auto_approved') {
    statusLabel = 'Upload Ticket'; pillClass = 'bg-amber-100 text-amber-700';
  } else {
    statusLabel = 'Awaiting Approval'; pillClass = 'bg-gray-100 text-gray-600';
  }

  return (
    <button
      type="button"
      onClick={onTrack}
      className="w-full text-left bg-white border border-gray-200 rounded-lg p-3 flex items-start justify-between gap-2 hover:border-blue-300 hover:bg-blue-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className="w-8 h-8 bg-amber-100 rounded-full flex-shrink-0 flex items-center justify-center">
          <ArrowRight className="w-4 h-4 text-amber-600" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">
            {formatDate(extension.leave_start_date)} → {formatDate(extension.leave_end_date)}
          </div>
          <div className="mt-0.5 text-xs text-gray-500">
            {totalDays > 0 ? (
              <DurationWithConsumption
                proposal={extension.leave_consumption_proposal}
                showBreakdown={hasCrossTypeConsumption(extension, leaveTypeName)}
                justify="start"
              >
                {formatLeaveDays(totalDays)} {pluralizeDays(totalDays)}
              </DurationWithConsumption>
            ) : 'Processing…'}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${pillClass}`}>{statusLabel}</span>
        <ChevronRight className="w-4 h-4 text-gray-400" />
      </div>
    </button>
  );
};

const Step5Complete = forwardRef(({ leaveType, leave, onTrackExtension, onExtensionBtnChange }, ref) => {
  const excludedLeaveDates = useLeaveExcludedDates(leave, Boolean(leave));
  const isHomeLeave = leaveType === 'home_leave';
  const isUrgentLeave = leaveType === 'urgent_leave';

  // ── Extension state ────────────────────────────────────────────────────────
  const [showExtForm, setShowExtForm] = useState(false);
  const [extEndDate, setExtEndDate] = useState('');
  const [extReason, setExtReason] = useState('');
  // Newly submitted extensions are tracked locally until parent re-fetches
  const [localExtensions, setLocalExtensions] = useState([]);
  const extensionQueryEnabled = (
    (isHomeLeave || isUrgentLeave)
    && Boolean(leave?.id)
    && !leave?.is_extension
  );
  const {
    data: eligibility,
    isLoading: loadingEligibility,
    isFetching: fetchingEligibility,
    isError: eligibilityFailed,
    refetch: refetchEligibility,
  } = useLeaveExtensionEligibility(leave?.id, {
    enabled: extensionQueryEnabled,
  });
  const {
    data: extPreview,
    isLoading: loadingExtPreview,
    isFetching: fetchingExtPreview,
    isError: extPreviewFailed,
    refetch: refetchExtPreview,
  } = useLeaveExtensionPreview(leave?.id, extEndDate, {
    enabled: extensionQueryEnabled && showExtForm,
  });
  const extPreviewNotes = getOverseasLeaveSplitNotes(extPreview);
  const requestExtensionMutation = useRequestLeaveExtension();
  const submittingExt = requestExtensionMutation.isPending;
  const extensionLeaveTypeName = isHomeLeave ? 'Home Leave' : 'Urgent Leave';

  const extensionBtnVisible = !loadingEligibility && !!eligibility?.eligible && !showExtForm
    && (isHomeLeave || isUrgentLeave) && !leave?.is_extension;

  useImperativeHandle(ref, () => ({
    requestExtension: () => setShowExtForm(true),
  }), []);

  useEffect(() => {
    onExtensionBtnChange?.(extensionBtnVisible);
  }, [extensionBtnVisible, onExtensionBtnChange]);

  const handleSubmitExtension = async () => {
    if (!extEndDate || !extReason.trim()) return;
    try {
      const result = await requestExtensionMutation.mutateAsync({
        applicationId: leave.id,
        data: {
          new_end_date: extEndDate,
          extension_reason: extReason.trim(),
        },
      });
      const newExt = result.extension_application;
      if (newExt) {
        setLocalExtensions(prev => [...prev, newExt]);
      }
      setShowExtForm(false);
      setExtEndDate('');
      setExtReason('');
    } catch {
      // The mutation owns the employee-facing error toast. Keep the form open
      // so the worker can correct the request or retry without re-entering it.
    }
  };

  // Merge server-side existing extensions with any newly submitted ones
  const serverExtensions = eligibility?.existing_extensions || [];
  const allExtensions = [
    ...serverExtensions,
    ...localExtensions.filter(e => !serverExtensions.some(s => s.id === e.id)),
  ];

  const today = getTodayInSingaporeTimezone();
  const leaveHasStarted = leave?.leave_start_date && leave.leave_start_date <= today;

  return (
    <div className="space-y-5">
      {/* ── Completion banner ─────────────────────────────────────────── */}
      <div className="text-center space-y-2">
        <CheckCircle className="w-14 h-14 text-green-600 mx-auto" />
        <div className="text-green-700 font-semibold text-lg">Leave Activated</div>
        <div className="text-sm text-gray-600">
          {isHomeLeave
            ? 'HR has verified your flight ticket. Your annual leave and unpaid leave balances have been deducted.'
            : isUrgentLeave
            ? 'Your urgent leave has been processed. Leave balances have been adjusted.'
            : 'Your leave has been approved and balance deducted.'}
        </div>
      </div>

      {/* ── Leave summary card ────────────────────────────────────────── */}
      {leave && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Leave Summary</div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-500 text-xs">Type</span>
              <div className="font-medium">
                {leave.leave_type?.leave_type_name || (isHomeLeave ? 'Home Leave' : isUrgentLeave ? 'Urgent Leave' : 'Annual Leave')}
              </div>
            </div>
            <div>
              <span className="text-gray-500 text-xs">Duration</span>
              <div className="font-medium">
                {leave.leave_total_days} day{Number(leave.leave_total_days) !== 1 ? 's' : ''}
              </div>
            </div>
            <div>
              <span className="text-gray-500 text-xs">Start</span>
              <div className="font-medium">{formatDate(leave.leave_start_date)}</div>
            </div>
            <div>
              <span className="text-gray-500 text-xs">End</span>
              <div className="font-medium">{formatDate(leave.leave_end_date)}</div>
            </div>
          </div>
          <LeaveExcludedDaysCallout
            excludedDates={excludedLeaveDates}
            leaveTypeName={
              leave.leave_type?.leave_type_name
              || (isHomeLeave ? 'Home Leave' : isUrgentLeave ? 'Urgent Leave' : 'Annual Leave')
            }
            audience="employee"
          />
          {leave.leave_consumption_proposal?.items?.length > 0 && (
            <div className="pt-2 border-t border-gray-200">
              <div className="text-xs text-gray-500 mb-1">
                {leave.leave_consumption_proposal.future_year_projection?.supported
                  ? 'Balance Reserved'
                  : 'Balance Deducted'}
              </div>
              {leave.leave_consumption_proposal.items.map((item, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-gray-700">
                    {item.leave_type}
                    {item.balance_year ? ` (${item.balance_year})` : ''}
                  </span>
                  <span className="font-medium">{formatLeaveDays(item.days)} {pluralizeDays(item.days)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Home Leave / Urgent Leave Extension section ────────────────── */}
      {/* Hidden until leave period starts (or extensions exist) to avoid premature disclosure */}
      {(isHomeLeave || isUrgentLeave) && !leave?.is_extension && (leaveHasStarted || allExtensions.length > 0) && (
        <div className="space-y-3">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{isHomeLeave ? 'Home Leave' : 'Urgent Leave'} Extension</div>

          {/* Loading eligibility */}
          {loadingEligibility && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <LoadingSpinner size="sm" />
              <span>Checking extension eligibility…</span>
            </div>
          )}

          {/* A failed eligibility check must never look like "no action available". */}
          {!loadingEligibility && eligibilityFailed && !eligibility && (
            <div
              className="rounded-xl border border-red-200 bg-red-50 p-4"
              role="alert"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-red-900">
                    We couldn&apos;t check extension availability
                  </p>
                  <p className="mt-1 text-sm text-red-700">
                    Your leave is unchanged. Check your connection, then try again.
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchEligibility()}
                    disabled={fetchingEligibility}
                    className="btn btn-outline btn-sm mt-3"
                  >
                    <RefreshCw className={`h-4 w-4 ${fetchingEligibility ? 'animate-spin' : ''}`} />
                    {fetchingEligibility ? 'Checking…' : 'Try Again'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Give the worker a clear next step before opening the form. */}
          {!loadingEligibility && eligibility?.eligible && !showExtForm && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                  <Plane className="h-4 w-4 text-blue-800" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-blue-900">
                    Need to return later?
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-blue-800">
                    Request an extension from {formatDate(eligibility.min_new_end_date)}.
                    Choose your new return date and tell your approver why you need more time.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Ineligible reason — keep policy limits private but never leave a blank state. */}
          {!loadingEligibility && eligibility && !eligibility.eligible && (() => {
            const reason = eligibility.reason || '';
            const isPending = Boolean(eligibility.pending_extension_id);
            const isCapReason = reason.toLowerCase().includes('30 calendar') || reason.toLowerCase().includes('calendar days') || reason.toLowerCase().includes('cap');
            const employeeReason = isPending
              ? 'Your request below is with your approver. You can ask for more time once it has been decided.'
              : isCapReason
                ? 'No additional extension days are available for this trip. Contact HR if your return plans have changed.'
                : reason;
            return (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-start gap-3">
                  {isPending
                    ? <Clock className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-500" />
                    : <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-500" />}
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {isPending ? 'Extension awaiting approval' : 'Extension unavailable'}
                    </p>
                    <p className="mt-1 text-sm text-gray-600">
                      {employeeReason || 'An extension cannot be requested for this leave.'}
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Extension request form */}
          {showExtForm && eligibility?.eligible && (
            <div className="space-y-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
              <div>
                <div className="text-sm font-semibold text-blue-900">
                  Request {isHomeLeave ? 'Home Leave' : 'Urgent Leave'} Extension
                </div>
                <p className="mt-1 text-xs text-blue-700">
                  Additional leave starts automatically on {formatDate(eligibility.min_new_end_date)}.
                </p>
              </div>
              <div className="form-group">
                <label className="form-label">New Return Date</label>
                <DatePickerField
                  theme="employee"
                  selected={parseLocalDate(extEndDate)}
                  minDate={parseLocalDate(eligibility.min_new_end_date)}
                  maxDate={parseLocalDate(eligibility.max_new_end_date)}
                  onChange={(date) => setExtEndDate(date ? toLocalISODate(date) : '')}
                  placeholderText="Select new return date"
                />
              </div>
              {extEndDate && (
                <div className="rounded-lg border border-blue-200 bg-white p-3" aria-live="polite">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-700">Leave Breakdown</span>
                    {extPreview && (
                      <InfoBadge
                        color="blue"
                        size="xs"
                        label={`${formatLeaveDays(extPreview.calendar_days)} calendar ${pluralizeDays(extPreview.calendar_days)}`}
                      />
                    )}
                  </div>
                  {loadingExtPreview ? (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-blue-700">
                      <LoadingSpinner size="xs" />
                      <span>Calculating breakdown…</span>
                    </div>
                  ) : extPreview ? (
                    <>
                      <ConsumptionBreakdown proposal={extPreview.consumption_proposal} className="mt-2" />
                      <div className="mt-2 space-y-1 text-xs leading-relaxed text-gray-600">
                        {extPreviewNotes.map((note) => (
                          <p key={note}>{note}</p>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        The final split is confirmed when your extension is approved.
                      </p>
                    </>
                  ) : extPreviewFailed ? (
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                      <span className="text-xs text-red-700">
                        We couldn&apos;t calculate the breakdown. You can still submit.
                      </span>
                      <button
                        type="button"
                        onClick={() => refetchExtPreview()}
                        disabled={fetchingExtPreview}
                        className="btn btn-outline btn-sm"
                      >
                        <RefreshCw className={`h-4 w-4 ${fetchingExtPreview ? 'animate-spin' : ''}`} />
                        {fetchingExtPreview ? 'Checking…' : 'Try Again'}
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Reason for Extension</label>
                <textarea
                  className="textarea"
                  rows={3}
                  value={extReason}
                  onChange={e => setExtReason(e.target.value)}
                  placeholder="Briefly explain why you need additional time…"
                />
              </div>
              <div className="flex gap-2 btn-row-spread">
                <button
                  onClick={handleSubmitExtension}
                  disabled={submittingExt || !extEndDate || !extReason.trim()}
                  className="btn btn-primary"
                >
                  {submittingExt
                    ? <LoadingSpinner size="sm" color="white" />
                    : (
                      <>
                        <Send className="w-4 h-4 mr-1.5" />
                        Submit Request
                      </>
                    )
                  }
                </button>
                <button
                  onClick={() => { setShowExtForm(false); setExtEndDate(''); setExtReason(''); }}
                  disabled={submittingExt}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Existing extension cards */}
          {allExtensions.length > 0 && (
            <div className="space-y-2">
              {allExtensions.map(ext => (
                <ExtensionStatusCard
                  key={ext.id}
                  extension={ext}
                  leaveTypeName={extensionLeaveTypeName}
                  onTrack={() => onTrackExtension && onTrackExtension(ext)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <ApprovalTimeline leave={leave} />

    </div>
  );
});

const Step4Complete = ({ leaveType, leave }) => {
  const isHomeLeave = leaveType === 'home_leave';
  const isAnnualLeave = leaveType === 'annual_leave';
  const isUrgentLeave = leaveType === 'urgent_leave';
  const excludedLeaveDates = useLeaveExcludedDates(leave, Boolean(leave && isAnnualLeave));

  // Calculate duration
  const calculateDuration = () => {
    if (!leave) return '—';
    // Use backend-provided value if available
    if (leave.leave_total_days || leave.total_days) {
      const days = Number(leave.leave_total_days || leave.total_days);
      return `${formatLeaveDays(days)} ${pluralizeDays(days)}`;
    }
    // Fallback calculation
    const start = leave.leave_start_date ? new Date(leave.leave_start_date) : null;
    const end = leave.leave_end_date ? new Date(leave.leave_end_date) : start;
    if (!start || !end) return '—';
    let days = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1);
    if (leave.leave_start_time_period === 'PM') days -= 0.5;
    if (leave.leave_end_time_period === 'AM') days -= 0.5;
    return `${formatLeaveDays(days)} ${pluralizeDays(days)}`;
  };

  // Check if it's a half-day leave
  const isHalfDay = () => {
    if (!leave) return false;
    const days = Number(leave.leave_total_days || leave.total_days || 0);
    return days === 0.5 || days % 1 !== 0;
  };

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="text-center space-y-2">
        <CheckCircle className="w-14 h-14 text-green-600 mx-auto" />
        <div className="text-green-700 font-semibold text-lg">Application Complete</div>
        <div className="text-sm text-gray-600">
          {isHomeLeave 
            ? 'HR will process your reimbursement shortly.' 
            : isUrgentLeave
            ? 'Your urgent leave has been processed. Annual Leave and Unpaid Leave balances have been adjusted.'
            : 'Your leave has been approved and balance deducted.'}
        </div>
      </div>

      {/* Leave Details Card */}
      {leave && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Leave Details</h4>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            {/* Leave Type */}
            <div>
              <span className="text-gray-500">Type</span>
              <p className="font-medium text-gray-900">
                {leave.leave_type?.leave_type_name || (isHomeLeave ? 'Home Leave' : isUrgentLeave ? 'Urgent Leave' : 'Annual Leave')}
              </p>
            </div>
            
            {/* Total Days */}
            <div>
              <span className="text-gray-500">Total Days</span>
              <p className="font-medium text-gray-900">{calculateDuration()}</p>
            </div>
            
            {/* Start Date */}
            <div>
              <span className="text-gray-500">Start Date</span>
              <div className="flex items-center gap-2">
                <p className="font-medium text-gray-900">{formatDate(leave.leave_start_date)}</p>
                {isAnnualLeave && isHalfDay() && (
                  <SessionPeriodChip session={leave.leave_start_time_period === 'PM' ? 'PM' : null} />
                )}
              </div>
            </div>
            
            {/* End Date */}
            <div>
              <span className="text-gray-500">End Date</span>
              <div className="flex items-center gap-2">
                <p className="font-medium text-gray-900">{formatDate(leave.leave_end_date)}</p>
                {isAnnualLeave && isHalfDay() && (
                  <SessionPeriodChip session={leave.leave_end_time_period === 'AM' ? 'AM' : null} />
                )}
              </div>
            </div>
            
            {/* Status */}
            <div className="col-span-2">
              <span className="text-gray-500">Status</span>
              <div className="mt-1">
                <StatusBadge status="approved" />
              </div>
            </div>
          </div>
          <LeaveExcludedDaysCallout
            excludedDates={excludedLeaveDates}
            leaveTypeName={
              leave.leave_type?.leave_type_name
              || (isHomeLeave ? 'Home Leave' : isUrgentLeave ? 'Urgent Leave' : 'Annual Leave')
            }
            audience="employee"
          />
        </div>
      )}

      <ApprovalTimeline leave={leave} />

    </div>
  );
};

const WorkerLeaveModal = ({
  isOpen,
  onClose,
  leaveType,
  preselectedLeave,
  initialDates,
  onApplicationCreated,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [viewingStep, setViewingStep] = useState(null);
  const [leave, setLeave] = useState(preselectedLeave || null);
  // Tracks the original leave when worker switches to view an extension's workflow
  const originalLeaveRef = useRef(null);

  // Step 1 footer wiring: ref holds submit handler; state drives the disabled flag
  const step1SubmitRef = useRef(null);
  const [step1Disabled, setStep1Disabled] = useState(true);

  // Step 5 footer wiring: ref exposes requestExtension(); state drives button visibility
  const step5Ref = useRef(null);
  const [extensionBtnVisible, setExtensionBtnVisible] = useState(false);

  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  // ── Cancel leave state ────────────────────────────────────────────────
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');

  const cancelLeaveMutation = useMutation({
    mutationFn: ({ id, data }) => staffAPI.cancelLeaveApplication(id, data),
    onSuccess: () => {
      toast.success('Leave application cancelled successfully! Balance restored.');
      queryClient.invalidateQueries(['leave']);
      queryClient.invalidateQueries({ queryKey: ['pendingDocApprovalsSummary'] });
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to cancel leave application');
    },
  });

  const handleCancelLeave = () => {
    if (!leave?.id) return;
    cancelLeaveMutation.mutate(
      { id: leave.id, data: { cancellation_reason: cancellationReason } },
      { onSettled: () => { setShowCancelConfirm(false); setCancellationReason(''); } },
    );
  };

  // ── Flight ticket upload cleanup on close ─────────────────────────────
  const flightUploadStateRef = useRef(null);
  // Tracks whether the flight step has a document in preview/verify mode.
  // Drives the split-screen sizing: compact upload prompt until a ticket is
  // picked, then expands to the full document + OCR-form workspace.
  const [flightDocActive, setFlightDocActive] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const { isDiscarding, safeDiscardAndClose } = useSafeDiscardAndClose();

  const isHomeLeave = leaveType === 'home_leave';
  const isAnnualLeave = leaveType === 'annual_leave';
  const isUrgentLeave = leaveType === 'urgent_leave';
  // Home Leave and Urgent Leave have 5 steps; Annual Leave has 3 steps
  const hasFlightStep = isHomeLeave || isUrgentLeave;
  const hasHRVerificationStep = isHomeLeave || isUrgentLeave; // Both require HR to verify the flight ticket

  // Sync leave state when preselectedLeave prop changes
  useEffect(() => {
    setLeave(preselectedLeave || null);
    originalLeaveRef.current = null;
  }, [preselectedLeave]);

  // Called from Step5Complete when a worker taps an extension card
  const handleTrackExtension = useCallback((extensionLeave) => {
    originalLeaveRef.current = leave;
    setLeave(extensionLeave);
  }, [leave]);

  // Return from extension tracking back to the original leave
  const handleBackToOriginal = useCallback(() => {
    if (originalLeaveRef.current) {
      setLeave(originalLeaveRef.current);
      originalLeaveRef.current = null;
    }
  }, []);

  // Derive current step from leave state
  useEffect(() => {
    if (!leave) {
      setCurrentStep(1);
      return;
    }

    const verif = leave.flight_ticket_verification;
    const ticketSubmitted = !!leave.flight_details_submitted_at;
    const ticketApproved = verif?.status === 'approved';
    const ticketRejected = verif?.status === 'rejected';

    if (leave.leave_status === 'rejected') {
      // Keep on step 2 to show rejection reason
      setCurrentStep(2);
      return;
    }

    if (isAnnualLeave) {
      if (leave.leave_status === 'approved') setCurrentStep(3);
      else setCurrentStep(2);
      return;
    }

    if (isUrgentLeave) {
      if (ticketApproved) setCurrentStep(5);
      else if (ticketRejected) setCurrentStep(3); // allow re-upload
      else if (ticketSubmitted) setCurrentStep(4); // HR review
      else if (leave.leave_status === 'approved') setCurrentStep(3);
      else setCurrentStep(2);
      return;
    }

    // Home Leave: 5 steps
    if (ticketApproved) {
      setCurrentStep(5);
    } else if (ticketRejected) {
      // Go back to ticket upload with rejection message
      setCurrentStep(3);
    } else if (ticketSubmitted) {
      setCurrentStep(4);
    } else if (leave.leave_status === 'approved') {
      setCurrentStep(3);
    } else {
      setCurrentStep(2);
    }
  }, [leave, isHomeLeave, isAnnualLeave, isUrgentLeave]);

  // Reset peek view whenever the authoritative step advances
  useEffect(() => { setViewingStep(null); }, [currentStep]);

  // The step actually rendered — peek overrides the current step
  const displayStep = viewingStep ?? currentStep;
  const isPeeking = viewingStep !== null && viewingStep !== currentStep;
  const isFlightStep = hasFlightStep && displayStep === 3 && !isPeeking;
  // Expand to the wide split-screen workspace only once a ticket is in
  // preview/verify; the pre-upload prompt stays a compact, focused card.
  const isFlightWorkspace = isFlightStep && flightDocActive;

  // Dynamic steps by leave type
  const steps = useMemo(() => {
    if (isHomeLeave) {
      return [
        { id: 1, name: 'Application', icon: FileText },
        { id: 2, name: 'Approval', icon: Clock },
        { id: 3, name: 'Flight Ticket', icon: Upload },
        { id: 4, name: 'HR Review', icon: Clock },
        { id: 5, name: 'Complete', icon: CheckCircle },
      ];
    }
    if (isUrgentLeave) {
      return [
        { id: 1, name: 'Application', icon: FileText },
        { id: 2, name: 'Approval', icon: Clock },
        { id: 3, name: 'Flight Ticket', icon: Upload },
        { id: 4, name: 'HR Review', icon: Clock },
        { id: 5, name: 'Complete', icon: CheckCircle },
      ];
    }
    // Annual Leave
    return [
      { id: 1, name: 'Application', icon: FileText },
      { id: 2, name: 'Approval', icon: Clock },
      { id: 3, name: 'Complete', icon: CheckCircle },
    ];
  }, [isHomeLeave, isUrgentLeave]);

  const maxStep = steps.length;
  const isFinalStep = currentStep === maxStep;

  const showCancelButton = isFinalStep && currentStep > 1 && leave && canCancelLeave(leave);

  const modalConfig = useMemo(() => {
    if (isUrgentLeave) {
      return { icon: AlertTriangle, iconBg: 'bg-red-100', iconColor: 'text-red-600', title: 'Urgent Leave', description: '14 calendar days — no advance notice required' };
    } else if (isHomeLeave) {
      return { icon: Home, iconBg: 'bg-amber-100', iconColor: 'text-amber-600', title: 'Home Leave', description: 'Up to 30 calendar days · Annual Leave + Home Leave' };
    } else {
      return { icon: Plane, iconBg: 'bg-blue-100', iconColor: 'text-blue-600', title: 'Annual Leave', description: 'Complete your leave request details below' };
    }
  }, [isHomeLeave, isUrgentLeave]);

  // Extract rejection reason from the flight ticket verification (used in Step 3 re-submission)
  const flightRejectionReason = useMemo(() => {
    if (!leave) return null;
    const v = leave.flight_ticket_verification;
    if (v?.status === 'rejected' && v.rejection_reason) return v.rejection_reason;
    return null;
  }, [leave]);

  const handleCloseRequest = useCallback(() => {
    if (flightUploadStateRef.current) {
      setShowCloseConfirm(true);
      return;
    }
    onClose();
  }, [onClose]);

  const handleConfirmClose = useCallback(async () => {
    const uploadState = flightUploadStateRef.current;
    await safeDiscardAndClose({
      discardAction: uploadState?.leaveId
        ? () => staffAPI.discardFlightTicketUpload(uploadState.leaveId)
        : undefined,
      isIgnorableError: (e) => e?.response?.status === 409,
      onBeforeClose: () => setShowCloseConfirm(false),
      onDiscardSuccess: () => toast.success('Uploaded document removed'),
      onClose: () => {
        flightUploadStateRef.current = null;
        onClose();
      },
      failureMessage: 'Could not remove uploaded document. Closing anyway; you can re-upload later.',
      logContext: 'Failed to discard flight ticket upload during modal close:',
    });
  }, [onClose, safeDiscardAndClose]);

  if (!isOpen) return null;

  const isViewingExtension = !!originalLeaveRef.current;

  return (
    <Portal>
      <div className="modal-overlay">
        <div className={`modal-container ${isFlightWorkspace ? 'modal-container-xl' : 'modal-container-md'} ${isFlightWorkspace && !isMobile ? 'h-[92vh] max-h-[96vh]' : ''} transition-[max-width] duration-300`}>
          <div className="modal-header">
            <div className="flex items-center gap-3">
              {/* Back breadcrumb when viewing an extension */}
              {isViewingExtension && (
                <button
                  onClick={handleBackToOriginal}
                  className="flex items-center gap-1 text-sm text-blue-700 hover:text-blue-900 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 rounded"
                  title="Back to original leave"
                >
                  <ChevronRight className="w-4 h-4 rotate-180" />
                  Back
                </button>
              )}
              <div className={`w-10 h-10 ${isViewingExtension ? 'bg-amber-100' : modalConfig.iconBg} rounded-full flex items-center justify-center`}>
                {isViewingExtension
                  ? <ArrowRight className="w-5 h-5 text-amber-600" />
                  : <modalConfig.icon className={`w-5 h-5 ${modalConfig.iconColor}`} />
                }
              </div>
              <div>
                <h3 className="modal-title">
                  {isViewingExtension ? `${isHomeLeave ? 'Home Leave' : 'Urgent Leave'} Extension` : modalConfig.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {isViewingExtension
                    ? `${formatDate(leave?.leave_start_date)} → ${formatDate(leave?.leave_end_date)}`
                    : modalConfig.description
                  }
                </p>
              </div>
            </div>
            <button onClick={handleCloseRequest} className="modal-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Split-screen: modal-content-split kills the shell scrollbar so only the form panel scrolls */}
          <div className={`modal-content${isFlightStep ? ' modal-content-split' : ''}`}>
            {/* Stepper — hidden on split-screen step (stepper is inside Step3); visible on final step so workers can peek back */}
            {!isFlightStep && (
              <HorizontalStepper
                steps={steps}
                currentStep={currentStep}
                viewingStep={isPeeking ? viewingStep : null}
                onStepClick={currentStep > 1 ? (stepId) => setViewingStep(stepId < currentStep ? stepId : null) : undefined}
              />
            )}

            {/* ── Peek mode: read-only summary of a past step ──────── */}
            {isPeeking && displayStep === 1 && (
              <ApplicationSummary leave={leave} leaveType={leaveType} onBack={() => setViewingStep(null)} />
            )}
            {isPeeking && displayStep === 2 && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2 text-sm text-blue-800">
                  <Clock className="w-4 h-4 flex-shrink-0" />
                  <span>Read-only view of the approval progress for this application.</span>
                </div>
                <Step2ApprovalProgress leave={leave} leaveType={leaveType} />
                <button
                  type="button"
                  onClick={() => setViewingStep(null)}
                  className="btn btn-outline w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  <ChevronRight className="w-4 h-4 mr-1.5 rotate-180" />
                  Back to Current Step
                </button>
              </div>
            )}
            {isPeeking && displayStep === 3 && hasFlightStep && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2 text-sm text-blue-800">
                  <Upload className="w-4 h-4 flex-shrink-0" />
                  <span>Read-only summary of your submitted flight ticket details.</span>
                </div>
                {leave?.flight_details_submitted_at ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Submitted Ticket Details</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      {leave.flight_departure_date && (
                        <div>
                          <span className="text-xs text-gray-500">Departure</span>
                          <div className="font-medium">{formatDate(leave.flight_departure_date)}</div>
                        </div>
                      )}
                      {leave.flight_return_date && (
                        <div>
                          <span className="text-xs text-gray-500">Return</span>
                          <div className="font-medium">{formatDate(leave.flight_return_date)}</div>
                        </div>
                      )}
                      {(leave.flight_departure_airport || leave.flight_arrival_airport) && (
                        <div className="col-span-2">
                          <span className="text-xs text-gray-500">Route</span>
                          <div className="font-medium">{leave.flight_departure_airport || '—'} → {leave.flight_arrival_airport || '—'}</div>
                        </div>
                      )}
                      {leave.flight_booking_reference && (
                        <div>
                          <span className="text-xs text-gray-500">Booking Reference</span>
                          <div className="font-medium font-mono">{leave.flight_booking_reference}</div>
                        </div>
                      )}
                      {leave.flight_departure_flight_number && (
                        <div>
                          <span className="text-xs text-gray-500">Outbound Flight</span>
                          <div className="font-medium">{leave.flight_departure_flight_number}</div>
                        </div>
                      )}
                      {leave.flight_arrival_flight_number && (
                        <div>
                          <span className="text-xs text-gray-500">Return Flight</span>
                          <div className="font-medium">{leave.flight_arrival_flight_number}</div>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 pt-2 border-t border-gray-200 text-xs text-gray-400">
                      Submitted {formatDateTimeCompact(leave.flight_details_submitted_at)}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-400 italic">
                    No flight ticket details submitted yet.
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setViewingStep(null)}
                  className="btn btn-outline w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  <ChevronRight className="w-4 h-4 mr-1.5 rotate-180" />
                  Back to Current Step
                </button>
              </div>
            )}
            {isPeeking && displayStep === 4 && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2 text-sm text-blue-800">
                  <Eye className="w-4 h-4 flex-shrink-0" />
                  <span>Read-only view of the HR verification status.</span>
                </div>
                <Step4HRVerification leave={leave} />
                <button
                  type="button"
                  onClick={() => setViewingStep(null)}
                  className="btn btn-outline w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  <ChevronRight className="w-4 h-4 mr-1.5 rotate-180" />
                  Back to Current Step
                </button>
              </div>
            )}

            {/* ── Step 1: Application Form (only when not peeking) ──── */}
            {!isPeeking && currentStep === 1 && (
              <Step1ApplicationForm
                leaveType={leaveType}
                initialDates={initialDates}
                submitRef={step1SubmitRef}
                onDisabledChange={setStep1Disabled}
                onSubmitted={(created) => {
                  setLeave(created);
                  onApplicationCreated?.(created);
                  setCurrentStep(2);
                }}
              />
            )}

            {/* ── Step 2: Approval Progress ────────────────────────── */}
            {!isPeeking && currentStep === 2 && (
              <Step2ApprovalProgress leave={leave} leaveType={leaveType} />
            )}

            {/* ── Step 3: Flight Ticket (Home / Urgent) OR Complete (Annual) ── */}
            {!isPeeking && currentStep === 3 && (
              <>
                {hasFlightStep ? (
                  <Step3FlightDetails
                    leave={leave}
                    leaveType={leaveType}
                    previousRejection={flightRejectionReason}
                    uploadStateRef={flightUploadStateRef}
                    onDocActiveChange={setFlightDocActive}
                    steps={steps}
                    currentStep={currentStep}
                    onStepClick={(stepId) => setViewingStep(stepId < currentStep ? stepId : null)}
                    onClose={handleCloseRequest}
                    onSubmitted={() => {
                      setCurrentStep(4);
                    }}
                  />
                ) : (
                  // Annual Leave: step 3 = complete
                  <Step4Complete leaveType={leaveType} leave={leave} />
                )}
              </>
            )}

            {/* ── Step 4: HR Verification (Home Leave & Urgent Leave) ── */}
            {!isPeeking && currentStep === 4 && (
              <>
                {hasHRVerificationStep ? (
                  <Step4HRVerification leave={leave} />
                ) : (
                  <Step4Complete leaveType={leaveType} leave={leave} />
                )}
              </>
            )}

            {/* ── Step 5: Complete (Home Leave & Urgent Leave) ──────── */}
            {!isPeeking && currentStep === 5 && (isHomeLeave || isUrgentLeave) && (
              <Step5Complete
                ref={step5Ref}
                leaveType={leaveType}
                leave={leave}
                onTrackExtension={handleTrackExtension}
                onExtensionBtnChange={setExtensionBtnVisible}
              />
            )}
          </div>

          {/* DS modal-footer: Cancel + Submit — Step 1 only (never during peek) */}
          {!isPeeking && currentStep === 1 && (
            <div className="modal-footer">
              <button
                onClick={handleCloseRequest}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={() => step1SubmitRef.current?.()}
                className="btn btn-primary"
                disabled={step1Disabled}
              >
                <Send className="w-4 h-4 mr-2" />
                Submit Application
              </button>
            </div>
          )}

          {/* DS modal-footer: Close (+ Cancel Leave + Request Extension when eligible) — completion steps only */}
          {!isPeeking && isFinalStep && currentStep > 1 && (
            <div className="modal-footer">
              {showCancelButton && (
                <button onClick={() => setShowCancelConfirm(true)} className="btn btn-warning">
                  <Ban className="w-4 h-4 mr-1.5" />
                  Cancel Leave
                </button>
              )}
              {extensionBtnVisible && (
                <button onClick={() => step5Ref.current?.requestExtension()} className="btn btn-primary">
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Request Extension
                </button>
              )}
              <button onClick={onClose} className="btn btn-secondary">
                <CheckCircle className="w-4 h-4 mr-2" />
                Close
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={showCloseConfirm}
        title="Discard Uploaded Document?"
        confirmLabel="Discard & Close"
        cancelLabel="Keep Editing"
        variant="danger"
        theme="employee"
        onConfirm={handleConfirmClose}
        onCancel={() => setShowCloseConfirm(false)}
        isLoading={isDiscarding}
      >
        <p className="text-sm text-gray-700">
          Your uploaded flight ticket and any extracted data will be removed.
          You can re-upload when you return to this step.
        </p>
      </ConfirmModal>

      <ConfirmModal
        isOpen={showCancelConfirm}
        title="Cancel Leave Application"
        confirmLabel="Cancel Leave"
        variant="danger"
        theme="employee"
        onConfirm={handleCancelLeave}
        onCancel={() => { setShowCancelConfirm(false); setCancellationReason(''); }}
        isLoading={cancelLeaveMutation.isPending}
      >
        <p className="text-sm text-gray-700">
          Are you sure you want to cancel this {leave?.leave_type?.leave_type_name || (isHomeLeave ? 'Home Leave' : isUrgentLeave ? 'Urgent Leave' : 'Annual Leave')} for {formatDate(leave?.leave_start_date)}? Your leave balance will be restored.
        </p>
        <div className="mt-3">
          <label htmlFor="worker-cancel-reason" className="block text-sm font-medium text-gray-700 mb-1">
            Reason for cancellation
          </label>
          <textarea
            id="worker-cancel-reason"
            rows={3}
            className="textarea"
            placeholder="Enter reason for cancellation..."
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
          />
        </div>
      </ConfirmModal>
    </Portal>
  );
};

export default WorkerLeaveModal;

